import { zodResolver } from "@hookform/resolvers/zod";
import { AnimatePresence, motion } from "framer-motion";
import {
  Activity,
  BookOpen,
  BrainCircuit,
  Calendar,
  Camera, Check, ChevronDown, ChevronRight,
  Circle, Clock, Copy, Cpu, Database, DoorOpen, Download, Eye,
  FileText, Fingerprint, GitBranch, Info, Layers, Layout, LayoutGrid,
  List, ListOrdered, Menu, Mic, MousePointer2, MoveHorizontal, PenTool,
  Plus,
  Power,
  Radio, RefreshCw, RotateCcw, Save,
  Scale,
  Scan, Settings2, Share2,
  Shield, ShieldAlert, Sliders, Sparkles, Table, Tag, Target, Terminal,
  Trash2, Trophy,
  Users,
  X, Zap
} from "lucide-react";
import mermaid from 'mermaid';
import { useEffect, useMemo, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { FaSearchMinus, FaSearchPlus } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { useParcoursCreator } from '../stores/useParcoursCreator';
import { usePesaParcours } from "../stores/usePesaParcours";
import { templates } from "../templates";
import { parcoursSchema } from "../utils/parcoursSchema";

const ENGINE_TEMPLATES = {
  "visual-comparator": {
    title: "Nouvel Observatoire visuel",
    images: [
      { url: "", label: "Image A" },
      { url: "", label: "Image B" },
      { url: "", label: "Image C" }
    ],
    sliderEnabled: true,
    annotationEnabled: true,
    zoomEnabled: true,
    gridOverlay: true
  },
  "table-builder": {
    columns: ["Indicateur", "Valeur", "Analyse"],
    rows: [["", "", ""]],
    allowExport: true
  },
  "dual-builder": {
    mode: "debate",
    timeLimit: 300,
    roles: ["Proposant", "Opposant"]
  },
  "social-field-mapper": {
    axes: { x: "Capital Économique", y: "Capital Culturel" },
    quadrants: ["Dominants", "Prétendants", "Exclus", "Populaires"],
    precision: "high"
  }
};

const ALLOWED_ENGINES = ["visual-comparator", "table-builder", "dual-builder", "social-field-mapper"];


const useCompletionStatus = (formData) => {
  return useMemo(() => {
    const p1Score = () => {
      let score = 0;
      if (formData.nom?.trim()) score++;
      if (formData.slug?.trim() && formData.slug !== "ATTENTE_GENERATION") score++;
      if (formData.description?.trim()) score++;
      if (formData.niveau) score++;
      return { score, max: 4, label: "IDENTITÉ" };
    };
    const p2Score = () => {
      const mods = formData.modules || [];
      if (mods.length === 0) return { score: 0, max: 3, label: "MODULES" };
      let score = 1;
      const hasGranules = mods.some(m => (m.granules || []).length > 0);
      if (hasGranules) score++;
      const hasObjectifs = mods.some(m => (m.objectifs || []).length > 0);
      if (hasObjectifs) score++;
      return { score, max: 3, label: "MODULES" };
    };
    const p3Score = () => {
      const mods = formData.modules || [];
      const activities = mods.flatMap(m => (m.granules || []).filter(g => g.type === "activity"));
      if (activities.length === 0) return { score: 0, max: 2, label: "ACTIVITÉS" };
      let score = 1;
      const hasSteps = activities.some(a => (a.resources?.steps || []).length > 0);
      if (hasSteps) score++;
      return { score, max: 2, label: "ACTIVITÉS" };
    };
    const p4Score = () => {
      const logic = formData.engineConfig?.progressionLogic;
      if (!logic) return { score: 0, max: 2, label: "PROGRESSION" };
      const weights = logic.weights || {};
      const total = (weights.quizzes || 0) + (weights.activities || 0) + (weights.capstone || 0);
      let score = 0;
      if (total === 100) score++;
      if (logic.minimumScoreToPass > 0) score++;
      return { score, max: 2, label: "PROGRESSION" };
    };
    const p5Score = () => {
      const cap = formData.capstone || {};
      let score = 0;
      if (cap.titre?.trim()) score++;
      if (cap.description?.trim()) score++;
      return { score, max: 2, label: "CAPSTONE" };
    };
    const protocols = [p1Score(), p2Score(), p3Score(), p4Score(), p5Score()];
    const totalScore = protocols.reduce((acc, p) => acc + p.score, 0);
    const totalMax = protocols.reduce((acc, p) => acc + p.max, 0);
    const getStatus = (p) => {
      if (p.score === 0) return "void";
      if (p.score === p.max) return "complete";
      return "partial";
    };
    return {
      protocols: protocols.map((p, i) => ({ ...p, id: i + 1, status: getStatus(p) })),
      globalPct: Math.round((totalScore / totalMax) * 100)
    };
  }, [formData]);
};

const tokens = (dm) => ({
  // Surfaces
  bg: dm ? "bg-zinc-950" : "bg-white",
  bgSub: dm ? "bg-zinc-900/60" : "bg-zinc-50",
  bgHover: dm ? "hover:bg-white/4" : "hover:bg-zinc-50",
  // Borders
  border: dm ? "border-white/8" : "border-zinc-200",
  borderAccent: dm ? "border-orange-500/30" : "border-orange-300",
  // Text
  text: dm ? "text-zinc-100" : "text-zinc-900",
  textSub: dm ? "text-zinc-400" : "text-zinc-500",
  textMuted: dm ? "text-zinc-600" : "text-zinc-400",
  // Input
  input: dm
    ? "bg-zinc-900 border-zinc-700 text-zinc-100 placeholder:text-zinc-600 focus:border-orange-400/60 focus:ring-1 focus:ring-orange-400/20"
    : "bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 shadow-sm",
  // Accent
  accent: "text-orange-500",
  accentBg: dm ? "bg-orange-500/10" : "bg-orange-50",
  accentBorder: dm ? "border-orange-500/25" : "border-orange-200",
});

function StatusRing({ status, size = 20 }) {
  const configs = {
    void: { color: "text-zinc-500", bg: "bg-transparent", border: "border-zinc-600", icon: null },
    partial: { color: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/50", icon: <Clock size={10} /> },
    complete: { color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/50", icon: <Check size={10} strokeWidth={3} /> },
  };
  const cfg = configs[status] || configs.void;
  return (
    <div
      className={`flex items-center justify-center rounded-full border ${cfg.bg} ${cfg.border}`}
      style={{ width: size, height: size }}
    >
      <span className={cfg.color}>{cfg.icon || <Circle size={8} />}</span>
    </div>
  );
}

function FieldLabel({ label, hint, required, darkMode }) {
  return (
    <div className="mb-1.5">
      <div className="flex items-center gap-1.5">
        <label className={`text-[11px] font-bold uppercase tracking-widest ${darkMode ? "text-zinc-300" : "text-zinc-700"}`}>
          {label}
        </label>
        {required && (
          <span className="text-[9px] font-bold text-red-400 border border-red-400/30 px-1 rounded-sm">requis</span>
        )}
      </div>
      {hint && (
        <p className={`text-[11px] mt-0.5 leading-snug ${darkMode ? "text-zinc-500" : "text-zinc-400"}`}>{hint}</p>
      )}
    </div>
  );
}

function AdvancedToggle({ open, onToggle, count, darkMode }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`w-full flex items-center gap-3 py-2.5 px-1 text-[11px] font-medium transition-colors group ${
        darkMode ? "text-zinc-500 hover:text-zinc-300" : "text-zinc-400 hover:text-zinc-600"
      }`}
    >
      <div className={`flex-1 h-px ${darkMode ? "bg-zinc-800" : "bg-zinc-200"}`} />
      <div className="flex items-center gap-2 flex-shrink-0">
        <Sparkles size={12} className={open ? "text-amber-500" : ""} />
        <span>{open ? "Masquer les options avancées" : `${count || ""} options avancées`}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </div>
      <div className={`flex-1 h-px ${darkMode ? "bg-zinc-800" : "bg-zinc-200"}`} />
    </button>
  );
}

function WorkspaceCard({ title, subtitle, icon: Icon, accent = 'orange', required = false, children, darkMode, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);

  const accentColors = {
    orange: { border: 'border-l-orange-400', icon: 'text-orange-500', title: darkMode ? 'text-zinc-100' : 'text-zinc-900' },
    emerald: { border: 'border-l-emerald-400', icon: 'text-emerald-500', title: darkMode ? 'text-zinc-100' : 'text-zinc-900' },
    red: { border: 'border-l-red-400', icon: 'text-red-500', title: darkMode ? 'text-zinc-100' : 'text-zinc-900' },
    purple: { border: 'border-l-purple-400', icon: 'text-purple-500', title: darkMode ? 'text-zinc-100' : 'text-zinc-900' },
    amber: { border: 'border-l-amber-400', icon: 'text-amber-500', title: darkMode ? 'text-zinc-100' : 'text-zinc-900' },
    sky: { border: 'border-l-sky-400', icon: 'text-sky-500', title: darkMode ? 'text-zinc-100' : 'text-zinc-900' },
  };
  const ac = accentColors[accent] || accentColors.orange;

  return (
    <div className={`rounded-lg border transition-all overflow-hidden ${
      open
        ? `${ac.border} border-l-2 border-y  ${darkMode ? 'border-white/8 bg-zinc-900/40' : 'border-zinc-200 bg-white shadow-sm'}`
        : `border-l-2 border-l-transparent border-y  ${darkMode ? 'border-white/5' : 'border-zinc-100'}`
    }`}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`w-full flex items-center justify-between px-5 py-4 transition-colors ${darkMode ? 'hover:bg-white/3' : 'hover:bg-zinc-50/70'}`}
      >
        <div className="flex items-start gap-3">
          <div className={`mt-0.5 p-1.5 rounded-md ${darkMode ? 'bg-zinc-800' : 'bg-zinc-100'}`}>
            <Icon size={14} className={open ? ac.icon : darkMode ? 'text-zinc-500' : 'text-zinc-400'} />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className={`text-[13px] font-semibold ${open ? ac.title : darkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{title}</span>
              {required && (
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${open ? 'text-red-400 border-red-400/30 bg-red-400/5' : 'text-zinc-500 border-zinc-600'}`}>REQUIS</span>
              )}
            </div>
            {subtitle && open && <p className={`text-[11px] mt-0.5 ${darkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>{subtitle}</p>}
          </div>
        </div>
        <ChevronDown size={14} className={`flex-shrink-0 transition-transform ${darkMode ? 'text-zinc-600' : 'text-zinc-400'} ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className={`px-5 pb-5  ${darkMode ? 'border-white/5' : 'border-zinc-100'}`}>
          <div className="pt-4">{children}</div>
        </div>
      )}
    </div>
  );
}

export function StudioSection({ title, icon: Icon, colorClass, children, isOpen, onToggle, darkMode }) {
  
  const styles = {
    container: darkMode
      ? isOpen ? "border-red-200/50 bg-zinc-900/80 shadow-[inset_0_0_20px_rgba(0,0,0,0.4)]" : "border-white/5 bg-transparent"
      : isOpen ? "border-red-300 bg-white shadow-sm" : "border-zinc-200 bg-transparent",
    button: darkMode
      ? isOpen ? "text-white" : "text-zinc-500 hover:text-zinc-300"
      : isOpen ? "text-zinc-900" : "text-zinc-500 hover:text-zinc-700",
    badge: darkMode
      ? isOpen ? "bg-red-500 text-white border-red-400" : "bg-zinc-800 text-zinc-500 border-zinc-700"
      : isOpen ? "bg-red-500 text-white border-red-700" : "bg-zinc-100 text-zinc-400 border-zinc-200"
  };
  return (
    <div className={`transition-all duration-200 border-l-4 ${isOpen ? "border-l-red-500" : "border-l-transparent"} border-y border-r ${styles.container}`}>
      <button type="button" onClick={onToggle} className={`w-full flex items-center justify-between p-3 transition-all ${styles.button}`}>
        <div className="flex items-center gap-3">
          <Icon size={16} className={`${isOpen ? colorClass || "text-red-400" : "text-zinc-600"}`} />
          <span className="text-[11px] font-black uppercase tracking-[0.15em]">{title}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className={`font-mono text-[12px] px-1.5 py-0.5 border uppercase tracking-tighter ${styles.badge}`}>
            {isOpen ? "ACTIVE_SESSION" : "STANDBY"}
          </div>
          <ChevronDown size={14} className={`transition-transform duration-300 ${isOpen ? "rotate-180 text-orange-200" : "text-zinc-600"}`} />
        </div>
      </button>
      {isOpen && (
        <div className="animate-in fade-in slide-in-from-top-1 duration-200">
          <div className={`p-5  ${darkMode ? "bg-black/60 border-red-200/10" : "bg-zinc-50/50 border-red-100"}`}>
            <div className="relative">{children}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function WorkspaceContextBar({ protocolLabel, protocolHint, stats = [], darkMode, completion }) {
  const dm = darkMode;
  const tk = tokens(dm);

  const statusColor = completion?.status === "complete"
    ? "text-emerald-500"
    : completion?.status === "partial"
    ? "text-amber-500"
    : dm ? "text-zinc-600" : "text-zinc-400";

  return (
    <div className={`px-6 py-3 flex items-center justify-between ${dm ? "bg-zinc-900/50 border-white/6" : "bg-zinc-50/80 border-zinc-200"}`}>
      {/* Breadcrumb lisible */}
      <div className="flex items-center gap-2">
        <span className={`text-[11px] font-medium ${dm ? "text-zinc-500" : "text-zinc-400"}`}>Constructeur</span>
        <ChevronRight size={10} className={dm ? "text-zinc-700" : "text-zinc-300"} />
        <span className={`text-[12px] font-semibold ${dm ? "text-orange-300" : "text-orange-600"}`}>{protocolLabel}</span>
      </div>

      <div className="flex items-center gap-5">
        {/* Hint contextuel */}
        {protocolHint && (
          <div className={`hidden md:flex items-center gap-1.5 text-[11px] ${dm ? "text-zinc-500" : "text-zinc-400"}`}>
            <Info size={11} className="text-orange-400/60 flex-shrink-0" />
            <span>{protocolHint}</span>
          </div>
        )}

        {/* Stats */}
        {stats.map((s, i) => (
          <div key={i} className={`text-[11px] ${dm ? "text-zinc-500" : "text-zinc-400"}`}>
            <span>{s.label} </span>
            <span className={`font-semibold ${statusColor}`}>{s.value}</span>
          </div>
        ))}

        {/* Barre de complétion */}
        {completion && (
          <div className="flex items-center gap-2">
            <div className={`h-1 w-20 rounded-full overflow-hidden ${dm ? "bg-zinc-800" : "bg-zinc-200"}`}>
              <div
                className={`h-full rounded-full transition-all duration-700 ${
                  completion.status === "complete" ? "bg-emerald-500" :
                  completion.status === "partial" ? "bg-amber-500" : "bg-zinc-500"
                }`}
                style={{ width: `${(completion.score / completion.max) * 100}%` }}
              />
            </div>
            <span className={`text-[10px] font-medium ${statusColor}`}>
              {completion.score}/{completion.max}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export function OptionsManager({ type, path, register, control, darkMode }) {
  
  const { fields, append, remove } = useFieldArray({ control, name: `${path}.options` });
  const styles = {
    container: `p-4 border transition-all duration-300 ${darkMode ? "bg-zinc-900/40 border-white/10" : "bg-zinc-50 border-zinc-200"}`,
    input: `w-full p-2.5 text-xs font-mono outline-none border transition-all ${darkMode ? "bg-black text-white border-white/10 focus:border-red-200" : "bg-white text-zinc-900 border-zinc-200 focus:border-red-600"}`,
    label: `text-[11px] font-black uppercase tracking-wider mb-2 block ${darkMode ? "text-orange-400/80" : "text-zinc-500"}`,
    btnGhost: `text-[11px] font-black uppercase tracking-tighter flex items-center gap-1 transition-colors ${darkMode ? "text-red-400 hover:text-orange-200" : "text-red-600 hover:text-red-700"}`
  };
  if (type === "open") {
    return (
      <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-400">
        <div className={styles.container}>
          <div className="flex items-center gap-2 mb-3"><Info size={14} className="text-orange-200" /><span className={styles.label}>Critères de validation</span></div>
          <textarea {...register(`${path}.validationCriteria`)} rows={4} className={styles.input} placeholder="Décrivez ce que l'apprenant doit mobiliser..." />
        </div>
      </div>
    );
  }
  if (type === "true-false") {
    return (
      <div className="flex gap-4 animate-in fade-in slide-in-from-top-2">
        {[{ label: "VRAI", value: "true" }, { label: "FAUX", value: "false" }].map((item) => (
          <label key={item.value} className="flex-1 cursor-pointer">
            <input type="radio" value={item.value} {...register(`${path}.correctAnswer`)} className="hidden peer" />
            <div className={`p-4 border text-center font-black uppercase text-xs tracking-widest transition-all ${darkMode ? "border-white/5 bg-white/5 text-zinc-500 hover:bg-white/10 peer-checked:bg-red-500 peer-checked:text-white" : "border-zinc-200 bg-white text-zinc-400 hover:bg-zinc-50 peer-checked:bg-red-500 peer-checked:text-white peer-checked:border-red-600"}`}>{item.label}</div>
          </label>
        ))}
      </div>
    );
  }
  return (
    <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
      <div className="flex justify-between items-center px-1">
        <label className={styles.label}>Options de réponse</label>
        <button type="button" onClick={() => append({ text: "", isCorrect: false })} className={styles.btnGhost}><Plus size={14} /> Ajouter</button>
      </div>
      <div className="space-y-2">
        {fields.map((field, idx) => (
          <div key={field.id} className="flex items-center gap-3 group animate-in slide-in-from-left-2">
            <input type="checkbox" {...register(`${path}.options.${idx}.isCorrect`)} className={`w-5 h-5 cursor-pointer appearance-none border transition-all ${darkMode ? "border-white/10 bg-black" : "border-zinc-300 bg-white"} checked:bg-red-500 checked:border-transparent`} />
            <input {...register(`${path}.options.${idx}.text`)} className={styles.input} placeholder={`Option ${idx + 1}...`} />
            <button type="button" onClick={() => remove(idx)} className="p-2 text-zinc-500 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"><X size={16} /></button>
          </div>
        ))}
        {fields.length === 0 && (
          <div className="text-center py-8 border-2 border-dashed border-white/5 rounded-lg">
            <p className="text-[11px] text-zinc-500 uppercase font-bold tracking-widest">Aucune option</p>
          </div>
        )}
      </div>
    </div>
  );
}

export const generateMermaidSyntax = (data) => {
  if (!data) return "graph LR\n   A[En attente de données...]";
  const clean = (str, limit = 48) => {
    if (!str) return "";
    return str.toString().replace(/<[^>]+>/g, ' ').normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[\n\r\t]+/g, ' ').replace(/[(){}[\]<>]/g, ' ').replace(/["'''‚‛""„‟]/g, "'").replace(/[^a-zA-Z0-9À-ÿ\s\-_']/g, ' ').replace(/\s+/g, ' ').trim().substring(0, limit);
  };
  const lines = ["graph LR"];
  lines.push("   classDef root fill:#0f172a,stroke:#f97316,stroke-width:4px,color:#fed7aa,font-weight:bold");
  lines.push("   classDef engine fill:#1e293b,stroke:#64748b,stroke-width:2px,color:#cbd5e1");
  lines.push("   classDef module fill:#1e293b,stroke:#fb923c,stroke-width:3px,color:#fed7aa");
  lines.push("   classDef granule fill:#111827,stroke:#f59e0b,stroke-width:2px,color:#fde68a");
  lines.push("   classDef activity fill:#1e293b,stroke:#d97706,stroke-width:3px,color:#fbbf24");
  lines.push("   classDef capstone fill:#450a0a,stroke:#ef4444,stroke-width:5px,color:#fca5a5");
  lines.push("   classDef fiche fill:#1e293b,stroke:#6b7280,stroke-width:2px,color:#9ca3af,font-style:italic");
  lines.push("   classDef note fill:#1e293b,stroke:#475569,stroke-width:1px,color:#cbd5e1,font-size:10px");
  lines.push("   classDef legend fill:none,stroke:none,color:#94a3b8,font-size:11px");
  const rootTitle = clean(data.nom || data.title || "PARCOURS");
  const rootSlug = clean(data.slug || '');
  const rootDesc = clean(data.description || '', 70);
  const rootComp = data.competences?.length ? clean(data.competences.join(', '), 60) : '';
  lines.push(`   P["${rootTitle.toUpperCase()}<br/><span style='font-size:9px'>${rootSlug}</span><br/><span style='font-size:10px'>${rootDesc}</span>"]:::root`);
  if (rootComp) lines.push(`   P --> COMP["Compétences clés<br/>${rootComp}"]:::note`);
  const niveau = clean(data.niveau || 'N/A');
  const duree = data.dureeEstimee ? `${data.dureeEstimee} min` : '?';
  lines.push(`   META["NIVEAU & PILOTAGE<br/>${niveau} • ${duree}"]:::engine`);
  lines.push(`   P --- META`);
  if (data.engineConfig) {
    const pass = data.engineConfig.progressionLogic?.minimumScoreToPass || 0;
    const harvest = data.engineConfig.enableDataHarvesting ? "Harvest ON" : "Harvest OFF";
    const seq = data.engineConfig.isSequencial ? "Séquentiel" : "Flexible";
    lines.push(`   ENG["MOTEUR<br/>Seuil: ${pass}% • ${seq} • ${harvest}"]:::engine`);
    lines.push(`   P --- ENG`);
  }
  let moduleCount = 0;
  if (data.modules?.length) {
    data.modules.forEach((mod, idx) => {
      moduleCount++;
      const mId = `M${moduleCount}`;
      const mTitle = clean(mod.titre || `Module ${idx + 1}`);
      const mDuree = mod.duree ? `${mod.duree} min` : '?';
      lines.push(`   subgraph SUB_${mId} ["${mTitle}"]`);
      lines.push(`      direction TB`);
      lines.push(`      ${mId}["${mTitle}<br/>${mDuree}"]:::module`);
      if (mod.granules?.length) {
        mod.granules.forEach((gran, gIdx) => {
          if (gran.type?.toLowerCase().includes('capstone')) return;
          const gId = `${mId}G${gIdx + 1}`;
          const gTitle = clean(gran.titre || `Activité ${gIdx + 1}`);
          const gType = clean(gran.type || 'scenario', 15);
          const className = gType.toLowerCase().includes('activity') ? 'activity' : 'granule';
          lines.push(`      ${gId}["${gTitle}<br/>${gType}"]:::${className}`);
          lines.push(`      ${mId} --> ${gId}`);
        });
      }
      lines.push("   end");
      lines.push(`   P --> ${mId}`);
    });
  }
  let capstone = data.capstone || data.modules?.flatMap(m => m.granules || []).find(g => g.type === 'capstone');
  if (capstone) {
    const cTitle = clean(capstone.titre || "CAPSTONE FINAL");
    lines.push(`   CAP{{"🚩 ${cTitle.toUpperCase()}"}}:::capstone`);
    lines.push(`   P ==> CAP`);
    for (let i = 1; i <= moduleCount; i++) lines.push(`   M${i} ==> CAP`);
  }
  return lines.join("\n");
};

export const generateSequenceMermaid = (data) => {
  if (!data) return "sequenceDiagram\n    participant A as Chargement...";
  const lines = ["sequenceDiagram"];
  lines.push("    autonumber");
  lines.push("    participant U as Apprenant");
  lines.push("    participant UI as UI Layer");
  lines.push("    participant Store as UI Store");
  lines.push("    participant Core as Core Engine");
  lines.push("    participant API as Backend");
  lines.push("    participant Sim as Simulateur");
  lines.push("    U->>+UI: Ouvre parcours (:slug)");
  lines.push("    activate UI");
  lines.push("    UI->>+API: fetchParcoursSecure(slug, token)");
  lines.push("    activate API");
  lines.push("    API-->>-UI: { course, progress, engineConfig }");
  lines.push("    deactivate API");
  lines.push("    UI->>+Store: hydrateUIState(course, progress)");
  lines.push("    activate Store");
  lines.push("    Store->>+Core: new MasterParcoursEngine(course, progress)");
  lines.push("    activate Core");
  lines.push("    Core-->>-Store: engineState prêt");
  lines.push("    Store-->>-UI: focusFirstGranule()");
  lines.push("    UI-->>-U: Affiche parcours");
  if (data.engineConfig?.enableDataHarvesting) {
    lines.push("    opt Harvesting activé");
    lines.push("      U->>UI: Input utilisateur");
    lines.push("      UI->>Store: harvest(stepId, data)");
    lines.push("      Store->>Core: harvest(stepId, data)");
    lines.push("      Core-->>Store: _notifyStore()");
    lines.push("      Store->>API: asyncPersistHarvest(userId, stepId, data)");
    lines.push("    end");
  }
  if (data.capstone) {
    lines.push("    opt Capstone atteint");
    lines.push("      U->>UI: Termine capstone");
    lines.push("      UI->>Store: commitFinalProgress()");
    lines.push("      Store->>Core: finalize(capstoneResults)");
    lines.push("      Core->>Core: isCertified = true");
    lines.push("      Store->>API: storeCertification(userId, verifiedAt)");
    lines.push("      API-->>Store: certUUID + PDF");
    lines.push("      Store-->>UI: showSuccessModal(certificat, PESAS)");
    lines.push("    end");
  }
  return lines.join("\n");
};

export const ArchitectureModal = ({ data, onClose, darkMode }) => {
  const { engineerDiagramUnlocked, unlockEngineerDiagram } = usePesaParcours();
  const chartRef = useRef(null);
  const containerId = useRef(`mermaid-${Math.random().toString(36).slice(2, 9)}`);
  const [viewMode, setViewMode] = useState('flowchart');
  const [mmdSyntax, setMmdSyntax] = useState("graph LR\n   A[Chargement...]");
  const handleSwitchToEngineerView = async () => {
    if (engineerDiagramUnlocked) { setViewMode('sequence'); return; }
    try { await unlockEngineerDiagram(); setViewMode('sequence'); } catch (err) { alert("Accès réservé aux ingénieurs"); }
  };
  useEffect(() => {
  if (!data) return;
  
  // Capture sous un nom différent pour éviter le conflit avec la ref
  const currentContainerId = containerId.current;
  
  const syntax = viewMode === 'flowchart' 
    ? generateMermaidSyntax(data) 
    : generateSequenceMermaid(data);
  setMmdSyntax(syntax);
  
  mermaid.initialize({ /* ... mêmes options ... */ });

  const renderChart = async () => {
    const element = document.getElementById(currentContainerId);
    if (!element) return;
    try {
      element.innerHTML = '';
      element.removeAttribute('data-processed');
      const { svg } = await mermaid.render(`mermaid-${currentContainerId}`, syntax);
      element.innerHTML = svg;
    } catch (error) {
      element.innerHTML = `<pre class="text-red-400 text-xs p-4 bg-zinc-950/70 rounded font-mono">${syntax}</pre>`;
    }
  };

  const timer = setTimeout(renderChart, 150);
  
  return () => {
    clearTimeout(timer);
    const el = document.getElementById(currentContainerId);
    if (el) el.innerHTML = '';
  };
}, [data, darkMode, viewMode]);

  const copyToClipboard = () => navigator.clipboard.writeText(mmdSyntax);
  const downloadSVG = () => {
    const svg = document.querySelector(`#${containerId.current} svg`);
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = `arch-${data?.slug || 'parcours'}-${viewMode}-${new Date().toISOString().slice(0, 10)}.svg`; link.click();
    URL.revokeObjectURL(url);
  };

  
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4">
      <div className={`w-full max-w-6xl h-[90vh] flex flex-col border ${darkMode ? 'bg-zinc-950 border-white/10' : 'bg-white border-zinc-200'} rounded-sm shadow-2xl overflow-hidden`}>
        <div className="flex justify-between items-center p-4  border-white/5 bg-zinc-900/20">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-orange-500/10 border border-orange-500/20 rounded-sm"><Share2 size={16} className="text-orange-500" /></div>
            <div><h2 className="text-[11px] font-mono font-black uppercase tracking-[0.2em] text-orange-200">Architecture View</h2><p className="text-[9px] font-mono text-zinc-500 uppercase italic">{viewMode === 'flowchart' ? 'Pédagogique (Concepteur)' : 'Séquence Technique (Ingénieur)'}</p></div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-red-500/20 hover:text-red-400 transition-all rounded-sm"><X size={20} /></button>
        </div>
        <div className="flex  border-white/10 bg-zinc-900/30">
          <button onClick={() => setViewMode('flowchart')} className={`flex-1 py-3 text-[11px] font-mono uppercase tracking-wider ${viewMode === 'flowchart' ? '-2 border-orange-500 text-orange-300 font-bold' : 'text-zinc-500 hover:text-zinc-300'}`}>Vue Parcours (Conception)</button>
          <button onClick={handleSwitchToEngineerView} className={`flex-1 py-3 text-[11px] font-mono uppercase tracking-wider ${viewMode === 'sequence' ? '-2 border-red-500 text-red-300 font-bold' : 'text-zinc-500 hover:text-zinc-300'}`}>Vue Séquence (Ingénierie)</button>
        </div>
        <div className="flex-1 relative bg-[#050505] overflow-hidden group">
          <TransformWrapper initialScale={0.8} centerOnInit={true} minScale={0.3} maxScale={2.5} wheel={{ step: 0.1 }} doubleClick={{ disabled: true }}>
            {({ zoomIn, zoomOut, resetTransform }) => (
              <>
                <div className="absolute bottom-6 left-6 z-20 flex flex-col gap-2 opacity-40 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => zoomIn()} className="p-2.5 bg-zinc-900/80 border border-white/10 text-white hover:bg-orange-600/80 rounded transition-colors"><FaSearchPlus size={16} /></button>
                  <button onClick={() => zoomOut()} className="p-2.5 bg-zinc-900/80 border border-white/10 text-white hover:bg-orange-600/80 rounded transition-colors"><FaSearchMinus size={16} /></button>
                  <button onClick={() => resetTransform()} className="p-2.5 bg-zinc-900/80 border border-white/10 text-white hover:bg-orange-600/80 rounded transition-colors"><RotateCcw size={16} /></button>
                </div>
                <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }}>
                  <div className="flex items-center justify-center p-20 min-w-[1200px] min-h-[800px]">
                    <div id={containerId.current} className="mermaid" ref={chartRef}>{mmdSyntax}</div>
                  </div>
                </TransformComponent>
              </>
            )}
          </TransformWrapper>
        </div>
        <div className="p-4  border-white/5 flex justify-between items-center bg-zinc-900/50">
          <div className="flex items-center gap-4 text-[10px] font-mono text-zinc-400">
            <div className="flex items-center gap-2 px-3 py-1 bg-white/5 rounded-full border border-white/5"><div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" /><span>Live Render Ready</span></div>
            <span>{data?.modules?.length || 0} modules • {data?.modules?.reduce((sum, m) => sum + (m.granules?.length || 0), 0) || 0} granules</span>
          </div>
          <div className="flex gap-3">
            <button onClick={copyToClipboard} className="flex items-center gap-2 px-4 py-2 bg-zinc-900 hover:bg-zinc-800 text-[11px] font-mono uppercase tracking-tighter border border-white/10 transition-all rounded-sm"><Copy size={14} /> Copy Syntax</button>
            <button onClick={downloadSVG} className="flex items-center gap-2 px-6 py-2 bg-orange-600 hover:bg-orange-500 text-white text-[11px] font-black uppercase tracking-[0.1em] transition-all rounded-sm shadow-[0_0_15px_rgba(234,88,12,0.3)]"><Download size={14} /> Export SVG</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export function QuizStudio({ control, register, path, watch, setValue, title, darkMode }) {  
  const [activeQ, setActiveQ] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const { fields, append, remove } = useFieldArray({ control, name: `${path}.questions` });
  const uiStyle = {
    panel: darkMode ? "bg-zinc-950/90 border-white/10" : "bg-white border-zinc-200",
    sidebar: darkMode ? "bg-black/40 border-r border-white/5" : "bg-zinc-50 border-r border-zinc-200",
    activeTab: darkMode ? "bg-red-500/10 border-red-200/50 text-red-400" : "bg-red-50 border-red-600 text-red-700",
    textMain: darkMode ? "text-white" : "text-zinc-900",
    textDim: darkMode ? "text-zinc-500" : "text-zinc-400",
    input: darkMode ? "bg-zinc-900 border-white/10 text-white" : "bg-white border-zinc-300 text-zinc-900",
  };
  if (!isEditing) {
    return (
      <div onClick={() => setIsEditing(true)} className={`group cursor-pointer border rounded-sm p-4 flex items-center justify-between transition-all ${uiStyle.panel} hover:border-red-200/50`}>
        <div className="flex items-center gap-4">
          <div className={`p-2 rounded-full ${darkMode ? "bg-red-500/10" : "bg-red-50"}`}><BrainCircuit size={18} className="text-orange-200" /></div>
          <div>
            <h4 className={`text-[12px] font-black uppercase tracking-widest ${uiStyle.textMain}`}>{title}</h4>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-[12px] font-mono text-orange-200 font-bold">{fields.length} UNITS</span>
              <div className="w-1 h-1 bg-zinc-700 rounded-full" />
              <span className={`text-[12px] font-mono uppercase ${uiStyle.textDim}`}>Threshold: {watch(`${path}.passingScore`)}%</span>
            </div>
          </div>
        </div>
        <div className={`text-[12px] font-black border px-3 py-1 transition-all ${darkMode ? "border-white/10 text-zinc-500 group-hover:text-white" : "border-zinc-200 text-zinc-400 group-hover:text-red-600"}`}>ACCESS_STUDIO</div>
      </div>
    );
  }
  return (
    <div className={`flex flex-col h-[650px] border shadow-2xl rounded-sm overflow-hidden animate-in zoom-in-95 duration-300 ${uiStyle.panel}`}>
      <div className={`flex items-center justify-between px-6 py-3 ${darkMode ? "border-white/5 bg-white/5" : "bg-zinc-50 border-zinc-200"}`}>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2"><BrainCircuit size={16} className="text-orange-200" /><span className={`text-[12px] font-black uppercase tracking-tighter ${uiStyle.textMain}`}>Evaluation_Engine</span></div>
          <div className="flex items-center gap-2 border-l border-white/10 pl-6"><span className={`text-[12px] font-mono ${uiStyle.textDim}`}>SCORE_REQUIS:</span><input type="number" {...register(`${path}.passingScore`)} className="w-12 text-[12px] font-black text-center outline-none bg-transparent border-red-200/30 text-orange-200" /><span className="text-[12px] text-orange-200/50 font-mono">%</span></div>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => { append({ text: "", type: "multiple-choice", points: 1, options: [] }); setActiveQ(fields.length); }} className="bg-red-500 hover:bg-red-500 text-white px-4 py-1.5 text-[12px] font-black uppercase tracking-tight flex items-center gap-2"><Plus size={12} strokeWidth={4} /> Add_Query</button>
          <button onClick={() => setIsEditing(false)} className={`p-1.5 hover:bg-red-500/10 hover:text-orange-200 transition-colors ${uiStyle.textDim}`}><X size={18} /></button>
        </div>
      </div>
      <div className="flex flex-1 overflow-hidden">
        <div className={`w-64 flex flex-col ${uiStyle.sidebar}`}>
          <div className="flex-1 overflow-y-auto overflow-x-auto p-2 space-y-1">
            {fields.map((q, idx) => (
              <button key={q.id} onClick={() => setActiveQ(idx)} className={`w-full flex items-center gap-3 p-3 text-left border transition-all rounded-sm ${activeQ === idx ? uiStyle.activeTab : "border-transparent opacity-60 hover:opacity-100"}`}>
                <span className="font-mono text-[12px] font-bold">{String(idx + 1).padStart(2, "0")}</span>
                <div className="flex-1 truncate"><p className="text-[12px] font-black uppercase truncate leading-none">{watch(`${path}.questions.${idx}.text`) || "NULL_DATA"}</p><p className="text-[12px] font-mono mt-1 opacity-50">TYPE::{watch(`${path}.questions.${idx}.type`)}</p></div>
                {activeQ === idx && <ChevronRight size={12} />}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto overflow-x-auto bg-transparent relative no-scrollbar">
          {fields[activeQ] ? (
            <div key={`question-editor-${activeQ}`} className="p-12 max-w-2xl mx-auto space-y-10 animate-in fade-in slide-in-from-bottom-2">
              <div className="flex justify-between items-end gap-8  border-white/5 pb-6">
                <div className="flex-1">
                  <label className={`text-[12px] font-mono mb-2 block ${uiStyle.textDim}`}>QUERY_STATEMENT</label>
                  <textarea {...register(`${path}.questions.${activeQ}.text`)} className={`w-full bg-transparent text-xl font-black uppercase tracking-tighter outline-none placeholder:opacity-10 ${uiStyle.textMain}`} rows={2} placeholder="ENTER_QUESTION_HERE" />
                </div>
                <div className="flex flex-col items-end gap-4">
                  <div className="text-right"><label className={`text-[12px] font-mono mb-1 block ${uiStyle.textDim}`}>WEIGHT</label><input type="number" {...register(`${path}.questions.${activeQ}.points`)} className={`w-12 text-xs font-black p-1 text-center  ${uiStyle.input} bg-transparent`} /></div>
                  <button onClick={() => { remove(activeQ); setActiveQ(Math.max(0, activeQ - 1)); }} className="text-orange-200/40 hover:text-orange-200 transition-colors"><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {["multiple-choice", "true-false", "open"].map((type) => (
                  <button key={type} type="button" onClick={() => setValue(`${path}.questions.${activeQ}.type`, type)} className={`py-2 text-[12px] font-black uppercase border transition-all ${watch(`${path}.questions.${activeQ}.type`) === type ? "border-red-200 bg-red-500 text-white" : `${darkMode ? "border-white/5 text-zinc-600 hover:border-white/20" : "border-zinc-200 text-zinc-400 hover:bg-zinc-50"}`}`}>{type.replace("-", "_")}</button>
                ))}
              </div>
              <div className={`p-6 border ${darkMode ? "bg-black/20 border-white/5" : "bg-white border-zinc-100"}`}>
                <OptionsManager type={watch(`${path}.questions.${activeQ}.type`)} path={`${path}.questions.${activeQ}`} register={register} control={control} themeColor="red" />
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center space-y-4 opacity-10"><BrainCircuit size={64} /><p className="text-[12px] font-black tracking-[0.5em] uppercase">Awaiting_Query_Selection</p></div>
          )}
        </div>
      </div>
    </div>
  );
}

export function WorkspaceStudio({ path, darkMode }) {
  const { formData, updateFormData } = useParcoursCreator();
  const [activeConfigTab, setActiveConfigTab] = useState("core");
  const { centralWorkspace, currentEngine, currentConfig } = useMemo(() => {
    const pathParts = path.split('.');
    let currentData = formData;
    for (const part of pathParts) {
      if (currentData === undefined || currentData === null) return { centralWorkspace: { type: "none", config: {} }, currentEngine: "none", currentConfig: {} };
      currentData = currentData[part];
    }
    const workspace = currentData?.centralWorkspace || { type: "none", config: {} };
    return { centralWorkspace: workspace, currentEngine: workspace.type || "none", currentConfig: workspace.config || {} };
  }, [formData, path]);
  const { control, register, watch, setValue, reset } = useForm({ values: { centralWorkspace: centralWorkspace } });
  useEffect(() => { reset({ centralWorkspace: centralWorkspace }); }, [centralWorkspace, reset]);
  const handleEngineChange = (newType) => {
    if (!ALLOWED_ENGINES.includes(newType)) return;
    updateFormData(`${path}.centralWorkspace.type`, newType);
    updateFormData(`${path}.centralWorkspace.config`, ENGINE_TEMPLATES[newType] || {});
  };
  const uiStyle = {
    panel: `rounded-xl border ${darkMode ? "bg-zinc-900 border-zinc-800" : "bg-white border-zinc-200 shadow-sm"}`,
    label: "text-[12px] font-black uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5",
    tabBtn: (active) => `px-4 py-2 text-[12px] font-bold uppercase tracking-tight transition-all -2 ${active ? "border-orange-500 text-orange-500" : "border-transparent text-zinc-500 hover:text-zinc-300"}`,
    input: `w-full bg-transparent  border-zinc-700 py-1.5 text-[11px] text-orange-300 outline-none focus:border-orange-500/50`,
  };
  const engines = [
    { id: "visual-comparator", name: "Visual Forensic", icon: Camera, description: "Comparaison d'images avec calques et annotations." },
    { id: "table-builder", name: "Data Architect", icon: Table, description: "Tableaux dynamiques, colonnes typées et export." },
    { id: "social-field-mapper", name: "Field Mapper", icon: MousePointer2, description: "Cartographie Bourdieu : axes, quadrants, marqueurs." },
    { id: "dual-builder", name: "Debate Arena", icon: Mic, description: "Débat structuré : arguments, positions, enregistrement." },
  ];
  return (
    <div className="grid grid-cols-12 gap-6 animate-in fade-in duration-500">
      <div className="col-span-12 lg:col-span-3 space-y-3">
        <label className={uiStyle.label}><Zap size={12} className="text-orange-500" /> Core_Engine_Selection</label>
        {engines.map((m) => (
          <button key={m.id} type="button" onClick={() => handleEngineChange(m.id)} className={`w-full p-4 rounded-xl border text-left transition-all group ${currentEngine === m.id ? "bg-orange-500/10 border-orange-500/50 shadow-lg shadow-orange-500/5" : "bg-zinc-950/40 border-zinc-800/50 hover:border-zinc-700"}`}>
            <div className="flex items-center gap-3 mb-2"><m.icon size={18} className={currentEngine === m.id ? "text-orange-400" : "text-zinc-600"} /><span className={`text-[11px] font-black uppercase tracking-widest ${currentEngine === m.id ? "text-white" : "text-zinc-500"}`}>{m.name}</span></div>
            <p className="text-[11px] text-zinc-500 leading-relaxed italic">{m.description}</p>
          </button>
        ))}
      </div>
      <div className="col-span-12 lg:col-span-9 flex flex-col gap-4">
        <div className={uiStyle.panel}>
          <div className="flex items-center justify-between px-6 py-2  border-zinc-800 bg-zinc-950/20">
            <div className="flex gap-4">
              <button onClick={() => setActiveConfigTab("core")} className={uiStyle.tabBtn(activeConfigTab === "core")}>Architecture</button>
              <button onClick={() => setActiveConfigTab("ux")} className={uiStyle.tabBtn(activeConfigTab === "ux")}>Interaction_Flow</button>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[550px]">
            <div className="p-8 border-r border-zinc-800 space-y-8 max-h-[600px] overflow-y-auto overflow-x-auto bg-zinc-900/20">
              {currentEngine === "visual-comparator" && (
                <div className="space-y-6 animate-in slide-in-from-left-4" key="visual-comparator-config">
                  <header><h3 className="text-[11px] font-bold text-white uppercase tracking-tighter">Forensic Setup</h3><p className="text-[12px] text-zinc-500">Images + calques collaboratifs</p></header>
                  <div className="space-y-5">
                    {[0, 1].map((i) => (
                      <div key={i} className="p-4 bg-black/40 border border-zinc-800 rounded-lg">
                        <label className={uiStyle.label}>Image {i === 0 ? "Alpha" : "Beta"} (URL)</label>
                        <input value={currentConfig.images?.[i]?.url || ""} onChange={(e) => { const newImages = [...(currentConfig.images || [{ url: "", label: "" }, { url: "", label: "" }])]; newImages[i] = { ...newImages[i], url: e.target.value }; updateFormData(`${path}.centralWorkspace.config.images`, newImages); }} className={uiStyle.input} placeholder={`https://...`} />
                        <label className={`${uiStyle.label} mt-3`}>Label</label>
                        <input value={currentConfig.images?.[i]?.label || ""} onChange={(e) => { const newImages = [...(currentConfig.images || [{ url: "", label: "" }, { url: "", label: "" }])]; newImages[i] = { ...newImages[i], label: e.target.value }; updateFormData(`${path}.centralWorkspace.config.images`, newImages); }} className={uiStyle.input} placeholder={i === 0 ? "Image A" : "Image B"} />
                      </div>
                    ))}
                    <div className="space-y-4 pt-4  border-zinc-800">
                      {[{ key: 'sliderEnabled', label: 'Slider principal' }, { key: 'annotationEnabled', label: 'Annotations' }, { key: 'zoomEnabled', label: 'Zoom + grille' }].map(opt => (
                        <label key={opt.key} className="flex items-center gap-2 text-[11px] font-mono cursor-pointer"><input type="checkbox" checked={currentConfig[opt.key] ?? false} onChange={(e) => updateFormData(`${path}.centralWorkspace.config.${opt.key}`, e.target.checked)} className="accent-orange-500" />{opt.label}</label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {currentEngine === "table-builder" && (
                <div className="space-y-6 animate-in slide-in-from-left-4" key="table-builder-config">
                  <header className="flex justify-between items-center"><h3 className="text-[11px] font-bold text-white uppercase tracking-tighter">Schema Architect</h3><button type="button" onClick={() => { updateFormData(`${path}.centralWorkspace.config.columns`, [...(currentConfig.columns || []), "Nouvelle colonne"]); }} className="text-[11px] bg-orange-500/20 text-orange-400 px-3 py-1 rounded border border-orange-500/30 font-bold">+ COLONNE</button></header>
                  <div className="space-y-3">{(currentConfig.columns || []).map((col, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-zinc-950 border border-zinc-800 rounded-lg">
                      <MoveHorizontal size={14} className="text-zinc-700" />
                      <input value={col} onChange={(e) => { const newCols = [...currentConfig.columns]; newCols[i] = e.target.value; updateFormData(`${path}.centralWorkspace.config.columns`, newCols); }} className="bg-transparent text-[11px] text-white flex-1 outline-none" />
                      <button type="button" onClick={() => updateFormData(`${path}.centralWorkspace.config.columns`, currentConfig.columns.filter((_, idx) => idx !== i))} className="text-red-400"><X size={14} /></button>
                    </div>
                  ))}</div>
                </div>
              )}
              {currentEngine === "dual-builder" && (
                <div className="space-y-6 animate-in slide-in-from-left-4" key="dual-builder-config">
                  <header><h3 className="text-[11px] font-bold text-white uppercase tracking-tighter">Debate Arena Setup</h3></header>
                  <div className="grid grid-cols-2 gap-6">
                    <div><label className={uiStyle.label}>Mode</label><select value={currentConfig.positionMode || "random"} onChange={(e) => updateFormData(`${path}.centralWorkspace.config.positionMode`, e.target.value)} className="w-full bg-zinc-900 text-[11px] p-2 border border-zinc-700 rounded"><option value="random">Aléatoire</option><option value="choice">Choix libre</option><option value="teacher">Attribué</option></select></div>
                    <div><label className={uiStyle.label}>Durée/tour (s)</label><input type="number" value={currentConfig.maxTurnDuration || 180} onChange={(e) => updateFormData(`${path}.centralWorkspace.config.maxTurnDuration`, Number(e.target.value))} className={uiStyle.input} /></div>
                  </div>
                  <div><label className={uiStyle.label}>Règles</label><textarea value={currentConfig.rules || ""} onChange={(e) => updateFormData(`${path}.centralWorkspace.config.rules`, e.target.value)} rows={4} className="w-full bg-transparent border border-zinc-700 rounded-xl p-3 text-[11px] font-mono resize-y" /></div>
                  <div className="flex flex-wrap gap-4 text-[11px] font-mono">
                    {[{ key: 'audioRecording', label: 'Enregistrement audio' }, { key: 'videoRecording', label: 'Vidéo' }, { key: 'anonymousMode', label: 'Mode anonyme' }].map(({ key, label }) => (
                      <label key={key} className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={currentConfig[key] ?? false} onChange={(e) => updateFormData(`${path}.centralWorkspace.config.${key}`, e.target.checked)} className="accent-orange-500" />{label}</label>
                    ))}
                  </div>
                </div>
              )}
              {currentEngine === "social-field-mapper" && (
                <div className="space-y-6 animate-in slide-in-from-left-4" key="social-field-mapper-config">
                  <h3 className="text-[11px] font-bold text-white uppercase tracking-tighter">Bourdieu Space Logic</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-zinc-950 border border-zinc-800 rounded"><label className={uiStyle.label}>Axe X</label><input value={currentConfig.axes?.x || ""} onChange={(e) => updateFormData(`${path}.centralWorkspace.config.axes`, { ...currentConfig.axes, x: e.target.value })} className={uiStyle.input} /></div>
                    <div className="p-3 bg-zinc-950 border border-zinc-800 rounded"><label className={uiStyle.label}>Axe Y</label><input value={currentConfig.axes?.y || ""} onChange={(e) => updateFormData(`${path}.centralWorkspace.config.axes`, { ...currentConfig.axes, y: e.target.value })} className={uiStyle.input} /></div>
                  </div>
                  <div><label className={uiStyle.label}>Quadrants (un par ligne)</label><textarea value={(currentConfig.quadrants || []).join("\n")} onChange={(e) => updateFormData(`${path}.centralWorkspace.config.quadrants`, e.target.value.split("\n").filter(Boolean))} rows={5} className="w-full bg-transparent border border-zinc-700 rounded-xl p-3 text-[11px] font-mono resize-y" /></div>
                </div>
              )}
              {currentEngine === "none" && <div className="h-full flex items-center justify-center text-zinc-500 text-[11px] font-mono italic">Sélectionnez un moteur</div>}
            </div>
            <div className="bg-black/60 flex flex-col relative overflow-hidden">
              <div className="p-4  border-zinc-800 flex justify-between items-center bg-zinc-950"><span className="text-[11px] font-black uppercase text-zinc-400 tracking-widest flex items-center gap-2"><Eye size={12} className="text-orange-500" /> Simulation (Live)</span></div>
              <div className="flex-1 p-8 flex items-center justify-center overflow-auto">
                <div className="w-full h-full border border-zinc-800 rounded bg-zinc-900 shadow-2xl overflow-hidden relative group">
                  {currentEngine === "visual-comparator" && (
                    <div className="w-full h-full flex flex-col">
                      <div className="flex-1 flex gap-4 p-4 bg-zinc-800/50">{currentConfig.images?.map((img, i) => (<div key={i} className="flex-1 relative rounded overflow-hidden border border-zinc-700">{img.url ? <img src={img.url} alt={img.label} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-zinc-600"><Camera size={48} /></div>}{img.label && <div className="absolute bottom-2 left-2 bg-black/60 px-2 py-1 text-[10px] text-white rounded">{img.label}</div>}</div>))}</div>
                    </div>
                  )}
                  {currentEngine === "table-builder" && (<div className="p-6 overflow-auto"><div className="grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-2">{currentConfig.columns?.map((col, i) => (<div key={i} className="h-10 bg-zinc-800/80 rounded flex items-center px-3 text-[12px] text-zinc-300 border border-zinc-700">{col}</div>))}</div></div>)}
                  {currentEngine === "dual-builder" && (<div className="w-full h-full p-8 flex flex-col items-center justify-center bg-gradient-to-br from-zinc-950 to-black"><Mic size={64} className="text-orange-400 mx-auto mb-8 opacity-80 animate-pulse" /><h4 className="text-[18px] font-black uppercase text-orange-200 mb-4">Débat Arena</h4></div>)}
                  {currentEngine === "none" && <div className="h-full flex items-center justify-center text-zinc-500 text-[12px] font-mono italic">Sélectionnez un moteur</div>}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-between items-center bg-black/20 p-4 rounded-xl border border-zinc-800/50">
          <div className="flex items-center gap-3"><div className="p-2 bg-zinc-800 rounded-lg"><Database size={16} className="text-zinc-400" /></div><div><p className="text-[12px] font-bold text-white uppercase tracking-tight">Persistence Engine</p><p className="text-[11px] text-zinc-500 font-mono">{path}.centralWorkspace</p></div></div>
          <button type="button" className="px-8 py-3 bg-orange-600 hover:bg-orange-500 text-white text-[11px] font-black uppercase tracking-widest rounded-full transition-all shadow-lg shadow-orange-500/20 flex items-center gap-2"><Save size={14} /> Sauvegarder Config</button>
        </div>
      </div>
    </div>
  );
}

export function ActivityStepsEditor({ path, darkMode }) {
  const { formData, updateFormData } = useParcoursCreator();
  const [activeIndex, setActiveIndex] = useState(0);
  const pathParts = `${path}.resources.steps`.split('.');
  let currentData = formData;
  for (const part of pathParts) { if (!currentData) break; currentData = currentData[part]; }
  const steps = Array.isArray(currentData) ? currentData : [];
  const currentStep = steps[activeIndex] || {};
  const questionsPath = `${path}.resources.questions`;
  let questionsData = formData;
  for (const part of questionsPath.split('.')) { if (!questionsData) break; questionsData = questionsData[part]; }
  const questions = Array.isArray(questionsData) ? questionsData : [];
  const handleAddStep = () => { const newSteps = [...steps, { title: "NOUVELLE_PHASE", duration: 15, type: "instruction", text: "", harvestKey: "", required: false }]; updateFormData(`${path}.resources.steps`, newSteps); setActiveIndex(steps.length); };
  const handleRemoveStep = (idx) => { updateFormData(`${path}.resources.steps`, steps.filter((_, i) => i !== idx)); setActiveIndex(Math.max(0, idx - 1)); };
  const handleUpdateStep = (idx, field, value) => { const newSteps = [...steps]; newSteps[idx] = { ...newSteps[idx], [field]: value }; updateFormData(`${path}.resources.steps`, newSteps); };
  const handleUpdateQuestion = (idx, field, value) => { const newQ = [...questions]; if (!newQ[idx]) newQ[idx] = { text: "", type: "open" }; newQ[idx] = { ...newQ[idx], [field]: value }; updateFormData(`${path}.resources.questions`, newQ); };
  const uiStyle = { label: "text-[11px] font-black uppercase tracking-[0.2em] text-orange-200 mb-2 block", field: darkMode ? "bg-black/20 border-white/10 text-white focus:border-red-200" : "bg-white border-zinc-200" };
  return (
    <div className={`flex h-[650px] border-2 rounded-sm overflow-hidden ${darkMode ? "bg-zinc-950 border-white/5" : "bg-white border-zinc-200"}`}>
      <div className={`w-64 flex flex-col border-r ${darkMode ? "border-white/5 bg-black/10" : "border-zinc-200 bg-zinc-50"}`}>
        <div className="p-4  border-white/5 flex justify-between items-center bg-black/10">
          <div><span className="text-[11px] font-black uppercase tracking-tighter text-orange-200">Flux_Activite</span><span className="text-[12px] font-mono opacity-40 uppercase block">Total: {steps.length}</span></div>
          <button type="button" onClick={handleAddStep} className="p-1.5 hover:bg-red-500 hover:text-white text-orange-200 transition-all border border-red-200/20"><Plus size={14} /></button>
        </div>
        <div className="flex-1 overflow-y-auto overflow-x-auto no-scrollbar">
          {steps.map((step, idx) => (
            <div key={`step-${idx}`} onClick={() => setActiveIndex(idx)} className={`relative group flex items-center gap-3 p-4 cursor-pointer transition-all  border-white/5 ${activeIndex === idx ? (darkMode ? "bg-red-500/10" : "bg-white") : "hover:bg-black/5"}`}>
              {activeIndex === idx && <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-500" />}
              <span className={`font-mono text-[11px] ${activeIndex === idx ? "text-orange-200" : "opacity-30"}`}>{String(idx + 1).padStart(2, "0")}</span>
              <div className="flex-1 min-w-0"><p className={`text-[11px] font-black uppercase truncate ${activeIndex === idx ? "text-white" : "text-zinc-500"}`}>{step.title || "EMPTY_PHASE"}</p><p className="text-[12px] font-mono opacity-30 truncate uppercase">{step.type || "instruction"}</p></div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto overflow-x-auto bg-transparent no-scrollbar">
        {steps[activeIndex] ? (
          <div key={`editor-${activeIndex}`} className="p-8 max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-right-4">
            <div className="flex items-end gap-4  border-white/5 pb-6">
              <div className="flex-1 space-y-2"><label className={uiStyle.label}>Désignation_Phase</label><input value={currentStep.title || ""} onChange={(e) => handleUpdateStep(activeIndex, 'title', e.target.value)} className={`w-full p-2 text-2xl font-black uppercase outline-none -2 bg-transparent transition-all ${darkMode ? "border-white/10 focus:border-red-200 text-white" : "border-zinc-200 focus:border-red-600"}`} placeholder="TITRE..." /></div>
              <div className="text-right"><span className="block text-[12px] font-mono opacity-30 uppercase">Node_Index</span><span className="text-[11px] font-mono text-orange-200 font-bold"># {String(activeIndex + 1).padStart(2, '0')}</span></div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2"><label className={uiStyle.label}>Type</label><select value={currentStep.type || "instruction"} onChange={(e) => handleUpdateStep(activeIndex, 'type', e.target.value)} className={`w-full p-2 text-[11px] font-mono outline-none border uppercase ${uiStyle.field}`}><option value="instruction">Instruction_Standard</option><option value="experiential-trigger">Trigger_Immersif</option><option value="progressive-reveal">Reveal_Process</option><option value="personal-synthesis">Synthese_Eleve</option></select></div>
              <div className="space-y-2"><label className={uiStyle.label}>Timer (Min)</label><input type="number" value={currentStep.duration || 15} onChange={(e) => handleUpdateStep(activeIndex, 'duration', Number(e.target.value))} className={`w-full p-2 text-[11px] font-mono outline-none border ${uiStyle.field}`} /></div>
            </div>
            <div className="space-y-2"><label className={uiStyle.label}>Instructions (Markdown)</label><textarea value={currentStep.text || ""} onChange={(e) => handleUpdateStep(activeIndex, 'text', e.target.value)} rows={8} className={`w-full p-5 text-[13px] font-mono leading-relaxed outline-none border transition-all resize-none ${uiStyle.field}`} placeholder="Décrivez l'exercice..." /></div>
            <div className={`p-6 border ${darkMode ? "bg-black/20 border-white/5" : "bg-zinc-50 border-zinc-200"}`}>
              <div className="flex items-center gap-2 mb-4"><PenTool size={14} className="text-orange-200" /><span className="text-[11px] font-black uppercase tracking-widest opacity-60">Spécifications_Livrable</span></div>
              <textarea value={questions[activeIndex]?.text || ""} onChange={(e) => handleUpdateQuestion(activeIndex, 'text', e.target.value)} className="w-full bg-transparent border-none focus:ring-0 text-xs italic opacity-80 text-red-200 font-mono" placeholder="Ex: 'Rédigez un document...'" rows={3} />
            </div>
            <div className="flex justify-between items-center pt-8  border-white/5">
              <div className="flex gap-8">
                <div><label className={uiStyle.label}>Harvest_Key</label><input value={currentStep.harvestKey || ""} onChange={(e) => handleUpdateStep(activeIndex, 'harvestKey', e.target.value)} className={`p-2 text-[11px] font-mono border ${uiStyle.field} w-40`} placeholder="key_id" /></div>
                <div className="flex items-end pb-2"><label className="flex items-center gap-2 cursor-pointer group"><input type="checkbox" checked={currentStep.required ?? false} onChange={(e) => handleUpdateStep(activeIndex, 'required', e.target.checked)} className="rounded-none border-zinc-700 bg-zinc-800 text-orange-200" /><span className="text-[11px] font-black uppercase opacity-40 group-hover:opacity-100">Required_Node</span></label></div>
              </div>
              <button type="button" onClick={() => handleRemoveStep(activeIndex)} className="group flex items-center gap-2 px-4 py-2 text-[11px] font-black border border-red-500/20 text-red-500 hover:bg-red-500 hover:text-white transition-all uppercase tracking-widest"><Trash2 size={12} /> Delete_Phase</button>
            </div>
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center opacity-20"><Layers size={60} className="text-orange-200" /><p className="mt-6 text-[11px] font-black uppercase tracking-[0.8em]">Wait_For_Selection</p></div>
        )}
      </div>
    </div>
  );
}

export function ContentStepsEditor({ control, register, stepPath, watch, darkMode, activeIndex, setActiveIndex }) {
  const { updateFormData } = useParcoursCreator();
  const { fields, append, remove } = useFieldArray({ control, name: `${stepPath}.steps` });
  const [enrichOpen, setEnrichOpen] = useState(false);
    const uiStyle = { label: "text-[11px] font-black uppercase tracking-[0.2em] text-orange-200 mb-2 block", field: darkMode ? "bg-black/20 border-white/10 text-white focus:border-red-200" : "bg-white border-zinc-200" };

  return (
    <div className={`flex h-[650px] border-2 rounded-sm overflow-hidden ${uiStyle.container}`}>
      <div className={`w-64 flex flex-col ${uiStyle.sidebar}`}>
        <div className="p-4  border-white/5 flex justify-between items-center bg-black/10">
          <div><span className="text-[11px] font-black uppercase tracking-tighter text-orange-200">Flux_Séquentiel</span><span className="text-[11px] font-mono opacity-50 uppercase block">Total : {fields.length}</span></div>
          <button type="button" onClick={() => append({ title: "NOUVELLE_ETAPE", text: "", type: "instruction", duration: 30 })} className="p-1.5 hover:bg-red-500 hover:text-white text-orange-200 transition-all border border-red-200/20"><Plus size={14} /></button>
        </div>
        <div className="flex-1 overflow-y-auto overflow-x-auto no-scrollbar">
          {fields.map((field, idx) => {
            const isActive = activeIndex === idx;
            return (
              <div key={field.id} onClick={() => { if (activeIndex === idx) { setActiveIndex(-1); setTimeout(() => setActiveIndex(idx), 0); } else setActiveIndex(idx); }} className={`relative group flex items-center gap-3 p-4 cursor-pointer transition-all  border-white/5 ${activeIndex === idx ? (darkMode ? "bg-red-500/10" : "bg-white") : "hover:bg-black/5"}`}>
                {isActive && <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-500" />}
                <span className={`font-mono text-[11px] ${isActive ? "text-orange-200" : "opacity-30"}`}>{String(idx + 1).padStart(2, "0")}</span>
                <div className="flex-1 min-w-0"><p className={`text-[11px] font-black uppercase truncate ${isActive ? "text-white" : "text-zinc-500"}`}>{field.title || "EMPTY_NODE"}</p><p className="text-[12px] font-mono opacity-30 truncate">{field.type || "instruction"}</p></div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto overflow-x-auto bg-transparent no-scrollbar">
        {fields[activeIndex] ? (
          <div key={`step-editor-${activeIndex}`} className="p-6 max-w-4xl mx-auto space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
            <div className="flex items-end gap-4  border-white/5 pb-5">
              <div className="flex-1 space-y-2"><label className={uiStyle.label}>Désignation_Étape</label><input {...register(`${stepPath}.steps.${activeIndex}.title`)} onChange={(e) => { register(`${stepPath}.steps.${activeIndex}.title`).onChange(e); updateFormData(`${stepPath}.steps.${activeIndex}.title`, e.target.value); }} className={`w-full p-3 text-2xl font-black uppercase outline-none -2 bg-transparent transition-all ${darkMode ? "border-white/10 focus:border-red-200 text-white" : "border-zinc-200 focus:border-red-600"}`} placeholder="NOM_DE_L'ÉTAPE..." /></div>
              <div className="text-right"><span className="block text-[11px] font-mono opacity-40 uppercase">REF_ID</span><span className="text-[11px] font-mono text-orange-200">{fields[activeIndex].id.split('-')[0]}</span></div>
            </div>
            <div className="grid grid-cols-2 gap-5">
              <div className="space-y-2"><label className={uiStyle.label}>Type_Protocole</label><select {...register(`${stepPath}.steps.${activeIndex}.type`)} className={`w-full p-2.5 text-[11px] font-mono outline-none border ${uiStyle.field}`}><option value="experiential-trigger">Déclencheur_Expérientiel</option><option value="instruction">Instruction_Technique</option><option value="theory">Contenu_Théorique</option></select></div>
              <div className="space-y-2"><label className={uiStyle.label}>Durée (s)</label><input type="number" {...register(`${stepPath}.steps.${activeIndex}.duration`)} className={`w-full p-2.5 text-[11px] font-mono outline-none border ${uiStyle.field}`} /></div>
            </div>
            <div className="space-y-2"><label className={uiStyle.label}>Contenu_Principal (Markdown)</label><textarea {...register(`${stepPath}.steps.${activeIndex}.text`)} rows={12} className={`w-full p-5 text-[13px] font-mono leading-relaxed outline-none border transition-all resize-none ${uiStyle.field}`} placeholder="Instructions, explications..." /></div>
            <div className="flex justify-between items-center pt-6  border-white/5">
              <div className="group"><label className={uiStyle.label}>Clé_Harvest</label><input {...register(`${stepPath}.steps.${activeIndex}.harvestKey`)} className={`mt-1.5 block p-2 text-[11px] font-mono border ${uiStyle.field} w-48`} placeholder="clef_unique" /></div>
              <button type="button" onClick={() => remove(activeIndex)} className="group flex items-center gap-2 px-4 py-2 text-[11px] font-black border border-red-500/30 text-red-500 hover:bg-red-500 hover:text-white transition-all uppercase tracking-widest"><Trash2 size={12} className="group-hover:animate-bounce" /> Détruire_Étape</button>
            </div>
            <div className={`pt-2  ${darkMode ? "border-white/5" : "border-zinc-100"}`}>
              <button type="button" onClick={() => setEnrichOpen(!enrichOpen)} className={`w-full flex items-center justify-between py-2.5 px-1 text-[11px] font-black uppercase tracking-widest transition-colors ${darkMode ? "text-amber-400 hover:text-amber-300" : "text-amber-600 hover:text-amber-700"}`}>
                <span className="flex items-center gap-2"><BrainCircuit size={12} /> Enrichissement PESACORE</span>
                <div className="flex items-center gap-3">
                  <span className={`text-[9px] font-mono px-2 py-0.5 border ${darkMode ? "border-amber-500/30 bg-amber-500/5" : "border-amber-600 bg-amber-50"}`}>PREMIUM</span>
                  <ChevronDown size={12} className={`transition-transform duration-200 ${enrichOpen ? "rotate-180" : ""}`} />
                </div>
              </button>
              {enrichOpen && (
                <div className="space-y-5 pt-2 pb-4 animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className={`p-4 border rounded-sm space-y-4 ${darkMode ? "border-amber-900/30 bg-amber-950/10" : "border-amber-200/60 bg-amber-50/20"}`}>
                    <span className="text-[10px] font-black uppercase tracking-widest text-amber-400 block">Méta Pédagogique</span>
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className="text-[10px] opacity-50 font-mono uppercase block mb-1">Type d'Ancrage</label>
                        <select {...register(`${stepPath}.steps.${activeIndex}.anchorType`)} className={`w-full p-2 text-[11px] font-mono border ${uiStyle.field}`}>
                          <option value="">--</option><option value="autobiographical">Autobiographique</option><option value="projective">Projectif</option><option value="observational">Observationnel</option>
                        </select>
                      </div>
                      <div><label className="text-[10px] opacity-50 font-mono uppercase block mb-1">Bloom Level</label>
                        <select {...register(`${stepPath}.steps.${activeIndex}.bloomLevel`)} className={`w-full p-2 text-[11px] font-mono border ${uiStyle.field}`}>
                          <option value="">--</option>
                          {["mémorisation", "compréhension", "application", "analyse", "évaluation", "création"].map(v => (<option key={v} value={v}>{v}</option>))}
                        </select>
                      </div>
                    </div>
                    <div><label className="text-[10px] opacity-50 font-mono uppercase block mb-1">Activation Cognitive</label>
                      <textarea {...register(`${stepPath}.steps.${activeIndex}.cognitiveActivation`)} rows={2} className={`w-full p-2 text-[11px] font-mono border resize-none ${uiStyle.field}`} placeholder="Activation mémoire épisodique → disponibilité émotionnelle..." />
                    </div>
                    <div><label className="text-[10px] opacity-50 font-mono uppercase block mb-1">Harvest Tag</label>
                      <input {...register(`${stepPath}.steps.${activeIndex}.harvestTag`)} className={`w-full p-2 text-[11px] font-mono border ${uiStyle.field}`} placeholder="vécu-violence-symbolique" />
                    </div>
                  </div>
                  <div className={`p-4 border rounded-sm space-y-4 ${darkMode ? "border-orange-900/30 bg-orange-950/10" : "border-orange-200/60 bg-orange-50/20"}`}>
                    <span className="text-[10px] font-black uppercase tracking-widest text-orange-400 block">Connexions PESACORE</span>
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className="text-[10px] opacity-50 font-mono uppercase block mb-1">Agent Echo</label>
                        <input {...register(`${stepPath}.steps.${activeIndex}.pesaEchoAgent`)} className={`w-full p-2 text-[11px] font-mono border ${uiStyle.field}`} placeholder="Yasmine" />
                      </div>
                      <div><label className="text-[10px] opacity-50 font-mono uppercase block mb-1">Unlock Condition</label>
                        <input {...register(`${stepPath}.steps.${activeIndex}.pesaUnlockCondition`)} className={`w-full p-2 text-[11px] font-mono border ${uiStyle.field}`} placeholder="Compléter la vidéo débloque..." />
                      </div>
                    </div>
                    <div><label className="text-[10px] opacity-50 font-mono uppercase block mb-1">Contexte Agent PESACORE</label>
                      <textarea {...register(`${stepPath}.steps.${activeIndex}.pesaEchoContext`)} rows={2} className={`w-full p-2 text-[11px] font-mono border resize-none ${uiStyle.field}`} placeholder="Yasmine vit exactement cela quand elle ferme son carnet..." />
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1"><label className="text-[10px] opacity-50 font-mono uppercase">Logique Adaptive</label><span className="text-[9px] text-amber-400 font-mono uppercase">ifEngaged / ifSkipped</span></div>
                      <div className="grid grid-cols-2 gap-2">
                        <textarea {...register(`${stepPath}.steps.${activeIndex}.adaptiveLogic.ifEngaged`)} rows={2} className={`w-full p-2 text-[11px] font-mono border resize-none ${uiStyle.field}`} placeholder="Si engagé : La vidéo révèle..." />
                        <textarea {...register(`${stepPath}.steps.${activeIndex}.adaptiveLogic.ifSkipped`)} rows={2} className={`w-full p-2 text-[11px] font-mono border resize-none ${uiStyle.field}`} placeholder="Si sauté : Les concepts essentiels..." />
                      </div>
                    </div>
                  </div>
                  <div className={`p-4 border rounded-sm space-y-4 ${darkMode ? "border-zinc-700/50 bg-black/20" : "border-zinc-200 bg-zinc-50"}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase tracking-widest opacity-50">Champs JSON Avancés</span>
                      <span className={`text-[9px] font-mono px-2 py-0.5 ${darkMode ? "bg-zinc-800 text-zinc-400" : "bg-zinc-200 text-zinc-600"}`}>Perdre focus pour sauvegarder</span>
                    </div>
                    <div><label className="text-[10px] opacity-50 font-mono uppercase block mb-1">Harvest Analysis <span className="text-amber-400">[object]</span></label>
                      <textarea key={`ha-${activeIndex}`} defaultValue={JSON.stringify(fields[activeIndex]?.harvestAnalysis || {}, null, 2)} onBlur={(e) => { try { updateFormData(`${stepPath}.steps.${activeIndex}.harvestAnalysis`, JSON.parse(e.target.value)); } catch (err) { } }} rows={4} className={`w-full p-2 text-[10.5px] font-mono border resize-y ${darkMode ? "bg-black/60 border-zinc-700/50 text-zinc-300" : "bg-zinc-950/90 border-zinc-700 text-zinc-200"}`} placeholder='{"highIntensity":"Profil Yasmine","lowIntensity":"Profil Lucas"}' />
                    </div>
                    <div><label className="text-[10px] opacity-50 font-mono uppercase block mb-1">Emotion Mapping <span className="text-amber-400">[object]</span></label>
                      <textarea key={`em-${activeIndex}`} defaultValue={JSON.stringify(fields[activeIndex]?.emotionMapping || {}, null, 2)} onBlur={(e) => { try { updateFormData(`${stepPath}.steps.${activeIndex}.emotionMapping`, JSON.parse(e.target.value)); } catch (err) { } }} rows={5} className={`w-full p-2 text-[10.5px] font-mono border resize-y ${darkMode ? "bg-black/60 border-zinc-700/50 text-zinc-300" : "bg-zinc-950/90 border-zinc-700 text-zinc-200"}`} />
                    </div>
                    <div><label className="text-[10px] opacity-50 font-mono uppercase block mb-1">Evaluation Grid <span className="text-amber-400">[array]</span></label>
                      <textarea key={`eg-${activeIndex}`} defaultValue={JSON.stringify(fields[activeIndex]?.evaluationGrid || [], null, 2)} onBlur={(e) => { try { updateFormData(`${stepPath}.steps.${activeIndex}.evaluationGrid`, JSON.parse(e.target.value)); } catch (err) { } }} rows={5} className={`w-full p-2 text-[10.5px] font-mono border resize-y ${darkMode ? "bg-black/60 border-zinc-700/50 text-zinc-300" : "bg-zinc-950/90 border-zinc-700 text-zinc-200"}`} />
                    </div>
                    <div><label className="text-[10px] opacity-50 font-mono uppercase block mb-1">Harvest Integration <span className="text-amber-400">[object]</span></label>
                      <textarea key={`hi-${activeIndex}`} defaultValue={JSON.stringify(fields[activeIndex]?.harvestIntegration || {}, null, 2)} onBlur={(e) => { try { updateFormData(`${stepPath}.steps.${activeIndex}.harvestIntegration`, JSON.parse(e.target.value)); } catch (err) { } }} rows={4} className={`w-full p-2 text-[10.5px] font-mono border resize-y ${darkMode ? "bg-black/60 border-zinc-700/50 text-zinc-300" : "bg-zinc-950/90 border-zinc-700 text-zinc-200"}`} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center opacity-30"><div className="relative"><Layers size={60} className="text-orange-200" /><div className="absolute inset-0 animate-ping border border-red-200 rounded-full opacity-20" /></div><p className="mt-6 text-[11px] font-black uppercase tracking-[0.8em] text-zinc-500">En attente de sélection</p></div>
        )}
      </div>
    </div>
  );
}

export function GranuleEditor({ control, register, modulePath, setValue, watch, darkMode }) {
  const { updateFormData } = useParcoursCreator();
  const { fields: granules, append, remove } = useFieldArray({ control, name: `${modulePath}.granules` });
  const [expandedIdx, setExpandedIdx] = useState(0);
  const [activeStudio, setActiveStudio] = useState("steps");
  const [activeStepByGranule, setActiveStepByGranule] = useState({});
  const uiStyle = {
    container: darkMode ? "bg-zinc-950/40 border-white/5" : "bg-white border-zinc-200 shadow-sm",
    header: darkMode ? "bg-zinc-900/50 border-white/10" : "bg-zinc-50 border-zinc-200",
    card: darkMode ? "bg-black/20 border-white/5" : "bg-white border-zinc-200",
    cardActive: darkMode ? "border-red-200/50 bg-red-500/5" : "border-red-300 bg-red-50/30",
    textMain: darkMode ? "text-white" : "text-zinc-900",
    textDim: darkMode ? "text-zinc-500" : "text-zinc-400",
    accent: "text-orange-200",
    btnPrimary: "bg-red-500 hover:bg-red-600 text-white",
    field: darkMode ? "border-zinc-700 focus:border-orange-500/70 bg-zinc-900/30" : "border-zinc-300 focus:border-orange-500/70 bg-white/50",
  };
  return (
    <div className={`space-y-4 ${uiStyle.container} border-none`}>
      <div className={`flex items-center justify-between p-4 border ${uiStyle.header}`}>
        <div><h3 className={`text-2xl font-black uppercase tracking-tighter flex items-center gap-3 ${uiStyle.textMain}`}><Layers size={22} className={uiStyle.accent} /> SÉQUENCEUR <span className="opacity-20">/</span> GRANULES</h3><p className={`text-[11px] font-mono uppercase tracking-[0.2em] mt-1 ${uiStyle.textDim}`}>Unités atomiques • {granules.length} segments</p></div>
        <button type="button" onClick={() => { const newIdx = granules.length; append({ id: `g-${Date.now()}`, type: "scenario", titre: "NOUVEAU_GRANULE", duration: 15, order: newIdx + 1, content: { steps: [], comprehensionQuiz: { title: "Quiz de validation", passingScore: 70, questions: [] } } }); setExpandedIdx(newIdx); }} className={`px-4 py-2 text-[11px] font-black uppercase tracking-widest flex items-center gap-2 transition-all ${uiStyle.btnPrimary}`}><Plus size={12} strokeWidth={3} /> INITIALISER_GRANULE</button>
      </div>
      <div className="space-y-3">
        {granules.map((granule, gIdx) => {
          const granulePath = `${modulePath}.granules.${gIdx}`;
          const isExpanded = expandedIdx === gIdx;
          const currentTitle = watch(`${granulePath}.titre`);
          const granuleType = watch(`${granulePath}.type`);
          return (
            <div key={granule.id} className={`transition-all border ${isExpanded ? uiStyle.cardActive : uiStyle.card}`}>
              <div className="flex justify-between items-center p-2 cursor-pointer group" onClick={() => setExpandedIdx(isExpanded ? null : gIdx)}>
                <div className="flex items-center gap-5 flex-1">
                  <div className="flex flex-col min-w-[60px]"><span className={`font-mono text-[11px] font-bold ${isExpanded ? uiStyle.accent : uiStyle.textDim}`}>NODE_{String(gIdx + 1).padStart(2, "00")}</span><span className="text-[11px] font-mono opacity-40">ID:{granule.id.split('-')[0]}</span></div>
                  <input value={currentTitle} onChange={(e) => { setValue(`${granulePath}.titre`, e.target.value); updateFormData(`${granulePath}.titre`, e.target.value); }} onClick={(e) => e.stopPropagation()} className={`text-[13px] font-black bg-transparent  transition-all ${isExpanded ? "border-red-200/50 w-1/3" : "border-transparent w-1/4"} ${uiStyle.textMain} outline-none focus:border-red-200 uppercase tracking-tight`} placeholder="TITRE_GRANULE" />
                  <div className="flex items-center gap-4 ml-auto mr-4">
                    <div className="flex flex-col items-start gap-0.5"><label className="text-[11px] font-black opacity-50 uppercase tracking-tighter">Type</label><select value={granuleType} onChange={(e) => { setValue(`${granulePath}.type`, e.target.value); updateFormData(`${granulePath}.type`, e.target.value); }} onClick={(e) => e.stopPropagation()} className={`bg-transparent text-[11px] font-mono font-bold uppercase outline-none cursor-pointer ${isExpanded ? "text-orange-200" : uiStyle.textDim}`}><option value="scenario">SCÉNARIO</option><option value="theory">THÉORIE</option><option value="workshop">ATELIER</option><option value="activity">ACTIVITÉ</option></select></div>
                    <div className="flex flex-col items-start gap-0.5"><label className="text-[11px] font-black opacity-50 uppercase tracking-tighter">Durée</label><div className="flex items-center gap-1"><input type="number" {...register(`${granulePath}.duration`)} onClick={(e) => e.stopPropagation()} className={`w-12 bg-transparent text-[11px] font-mono font-bold outline-none ${isExpanded ? "text-orange-200" : uiStyle.textDim}`} /><span className="text-[11px] opacity-50 font-mono">min</span></div></div>
                  </div>
                </div>
                <div className="flex items-center gap-3"><button type="button" onClick={(e) => { e.stopPropagation(); remove(gIdx); }} className="opacity-0 group-hover:opacity-100 p-1.5 text-zinc-500 hover:text-red-500 transition-all"><Trash2 size={14} /></button><ChevronDown size={18} className={`transition-transform ${isExpanded ? "rotate-180 text-orange-200" : uiStyle.textDim}`} /></div>
              </div>
              {isExpanded && (
                <div className="p-6  border-red-200/10 space-y-8 animate-in slide-in-from-top-2 duration-300">
                  {granuleType === "activity" ? (
                    <div className={`p-6 border-2 border-dashed rounded-sm flex flex-col items-center gap-4 ${darkMode ? "border-white/10 bg-white/5" : "border-zinc-200 bg-zinc-50"}`}><Zap size={32} className="text-orange-200 animate-pulse" /><div className="text-center"><p className={`font-black uppercase tracking-widest text-sm ${uiStyle.textMain}`}>Configuration Protocole 3 requise</p><p className={`text-[11px] font-mono mt-1 ${uiStyle.textDim}`}>Éditez dans l'onglet <span className="text-orange-200 underline">PROTOCOLE 3 : ACTIVITÉS</span></p></div></div>
                  ) : (
                    <>
                      <div className="space-y-4">
                        <div className="flex items-center gap-3"><span className={`text-[11px] font-black uppercase tracking-[0.3em] ${uiStyle.textMain}`}>01. ÉDITEUR_DE_FLUX</span><div className={`h-px flex-1 ${darkMode ? "bg-white/5" : "bg-zinc-200"}`} /></div>
                        <StudioSection title="SÉQUENCE_D_INTERACTION" icon={Layers} isOpen={activeStudio === "steps"} onToggle={() => setActiveStudio(activeStudio === "steps" ? null : "steps")} darkMode={darkMode}><ContentStepsEditor control={control} register={register} stepPath={`${granulePath}.content`} watch={watch} darkMode={darkMode} activeIndex={activeStepByGranule[granule.id] ?? 0} setActiveIndex={(newIndex) => setActiveStepByGranule(prev => ({ ...prev, [granule.id]: newIndex }))} /></StudioSection>
                      </div>
                      <div className="space-y-4">
                        <div className="flex items-center gap-3"><span className={`text-[11px] font-black uppercase tracking-[0.3em] ${uiStyle.textMain}`}>02. ENGINE_DE_VALIDATION</span><div className={`h-px flex-1 ${darkMode ? "bg-white/5" : "bg-zinc-200"}`} /></div>
                        <StudioSection title="STUDIO_DE_VÉRIFICATION" icon={BrainCircuit} isOpen={activeStudio === "quiz"} onToggle={() => setActiveStudio(activeStudio === "quiz" ? null : "quiz")} darkMode={darkMode}><QuizStudio control={control} register={register} path={`${granulePath}.content.comprehensionQuiz`} title={`VÉRIFICATION : ${currentTitle}`} watch={watch} setValue={setValue} darkMode={darkMode} /></StudioSection>
                      </div>
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <span className={`text-[11px] font-black uppercase tracking-[0.3em] ${uiStyle.textMain}`}>03. ENRICHISSEMENT_NARRATIF</span>
                          <div className={`h-px flex-1 ${darkMode ? "bg-white/5" : "bg-zinc-200"}`} />
                          <span className={`text-[9px] font-mono px-2 py-0.5 border ${darkMode ? "border-amber-500/30 text-amber-400" : "border-amber-600 text-amber-700"}`}>PESACORE IMPACT</span>
                        </div>
                        <StudioSection title="ARC_NARRATIF & MÉTA_PÉDAGOGIE" icon={BrainCircuit} colorClass="text-amber-400" isOpen={activeStudio === "narr"} onToggle={() => setActiveStudio(activeStudio === "narr" ? null : "narr")} darkMode={darkMode}>
                          <div className="space-y-5 p-4">
                            <div className={`p-4 border rounded-sm space-y-4 ${darkMode ? "border-amber-900/30 bg-amber-950/10" : "border-amber-200/60 bg-amber-50/20"}`}>
                              <span className="text-[10px] font-black uppercase tracking-widest text-amber-400 block">Arc Narratif</span>
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                  <label className="text-[10px] font-mono uppercase opacity-50 block">Position narrative</label>
                                  <select value={watch(`${granulePath}.narrativeArc.position`) || ''} onChange={(e) => updateFormData(`${granulePath}.narrativeArc.position`, e.target.value)} className={`w-full p-2 text-[11px] font-mono border ${uiStyle.field}`}>
                                    <option value="">-- Sélectionner --</option>
                                    <option value="exposition">Exposition</option><option value="développement">Développement</option><option value="résolution">Résolution</option><option value="climax">Climax</option>
                                  </select>
                                </div>
                                <div className="space-y-1.5">
                                  <div className="flex justify-between items-center"><label className="text-[10px] font-mono uppercase opacity-50 block">Tension Level</label><span className="text-[11px] font-mono text-amber-400 font-bold">{watch(`${granulePath}.narrativeArc.tensionLevel`) || 1}/5</span></div>
                                  <input type="range" min="1" max="5" step="1" value={watch(`${granulePath}.narrativeArc.tensionLevel`) || 1} onChange={(e) => updateFormData(`${granulePath}.narrativeArc.tensionLevel`, parseInt(e.target.value))} className="w-full accent-amber-500" />
                                </div>
                              </div>
                              <div className="space-y-1.5"><label className="text-[10px] font-mono uppercase opacity-50 block">Bascule Épistémique</label><input value={watch(`${granulePath}.narrativeArc.epistemicShift`) || ''} onChange={(e) => updateFormData(`${granulePath}.narrativeArc.epistemicShift`, e.target.value)} className={`w-full p-2 text-[11px] font-mono border ${uiStyle.field}`} placeholder="Passer de '...' à '...'" /></div>
                              <div className="space-y-1.5"><label className="text-[10px] font-mono uppercase opacity-50 block">Pont PESACORE</label><textarea value={watch(`${granulePath}.narrativeArc.transferBridge`) || ''} onChange={(e) => updateFormData(`${granulePath}.narrativeArc.transferBridge`, e.target.value)} rows={2} className={`w-full p-2 text-[11px] font-mono border resize-none ${uiStyle.field}`} placeholder="Connexion avec un agent ou situation PESACORE..." /></div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-1.5"><label className="text-[10px] font-mono uppercase opacity-50 block">Niveau Bloom</label><select value={watch(`${granulePath}.bloomLevel`) || ''} onChange={(e) => updateFormData(`${granulePath}.bloomLevel`, e.target.value)} className={`w-full p-2 text-[11px] font-mono border ${uiStyle.field}`}><option value="">--</option>{["mémorisation", "compréhension", "application", "analyse", "évaluation", "création"].map(v => (<option key={v} value={v}>{v}</option>))}</select></div>
                              <div className="space-y-1.5"><label className="text-[10px] font-mono uppercase opacity-50 block">Niveau Harvey</label><select value={watch(`${granulePath}.harveyTier`) || ''} onChange={(e) => updateFormData(`${granulePath}.harveyTier`, e.target.value)} className={`w-full p-2 text-[11px] font-mono border ${uiStyle.field}`}><option value="">--</option>{["savoir", "savoir-être", "savoir-faire"].map(v => (<option key={v} value={v}>{v}</option>))}</select></div>
                            </div>
                            <div className={`p-3 border rounded-sm space-y-3 ${darkMode ? "border-zinc-700/50 bg-black/20" : "border-zinc-200 bg-zinc-50"}`}>
                              <span className="text-[10px] font-black uppercase tracking-widest opacity-60 block">Signal Compétence</span>
                              <div className="grid grid-cols-2 gap-3">
                                <div><label className="text-[10px] font-mono opacity-40 block mb-1">Nom</label><input value={watch(`${granulePath}.competencySignal.name`) || ''} onChange={(e) => updateFormData(`${granulePath}.competencySignal.name`, e.target.value)} className={`w-full p-1.5 text-[11px] font-mono border ${uiStyle.field}`} /></div>
                                <div><label className="text-[10px] font-mono opacity-40 block mb-1">Niveau</label><select value={watch(`${granulePath}.competencySignal.level`) || ''} onChange={(e) => updateFormData(`${granulePath}.competencySignal.level`, e.target.value)} className={`w-full p-1.5 text-[11px] font-mono border ${uiStyle.field}`}><option value="">--</option><option value="débutant">Débutant</option><option value="intermédiaire">Intermédiaire</option><option value="avancé">Avancé</option><option value="expert">Expert</option></select></div>
                              </div>
                              <div><label className="text-[10px] font-mono opacity-40 block mb-1">Descripteur</label><input value={watch(`${granulePath}.competencySignal.descriptor`) || ''} onChange={(e) => updateFormData(`${granulePath}.competencySignal.descriptor`, e.target.value)} className={`w-full p-1.5 text-[11px] font-mono border ${uiStyle.field}`} /></div>
                            </div>
                            <div className={`p-3 border rounded-sm space-y-3 ${darkMode ? "border-orange-900/30 bg-orange-950/10" : "border-orange-200/60 bg-orange-50/20"}`}>
                              <span className="text-[10px] font-black uppercase tracking-widest text-orange-400 block">Connexions PESACORE</span>
                              <div><label className="text-[10px] font-mono opacity-50 block mb-1">ID Echo PESA</label><input value={watch(`${granulePath}.pesaEchoId`) || ''} onChange={(e) => updateFormData(`${granulePath}.pesaEchoId`, e.target.value)} className={`w-full p-1.5 text-[11px] font-mono border ${uiStyle.field}`} /></div>
                              <div><label className="text-[10px] font-mono opacity-50 block mb-1">Agent Dialogue Débloqué</label><input value={watch(`${granulePath}.unlocksPesaContent.agentDialogue`) || ''} onChange={(e) => updateFormData(`${granulePath}.unlocksPesaContent.agentDialogue`, e.target.value)} className={`w-full p-1.5 text-[11px] font-mono border ${uiStyle.field}`} /></div>
                              <div><label className="text-[10px] font-mono opacity-50 block mb-1">Hint Simulation</label><textarea value={watch(`${granulePath}.unlocksPesaContent.simulationHint`) || ''} onChange={(e) => updateFormData(`${granulePath}.unlocksPesaContent.simulationHint`, e.target.value)} rows={2} className={`w-full p-1.5 text-[11px] font-mono border resize-none ${uiStyle.field}`} /></div>
                            </div>
                          </div>
                        </StudioSection>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {granules.length === 0 && (
          <div className={`py-20 border-2 border-dashed rounded-sm flex flex-col items-center justify-center ${darkMode ? "border-white/5 opacity-30" : "border-zinc-200 opacity-50"}`}>
            <Layers size={48} className="mb-4 text-zinc-400" />
            <p className="font-mono text-[11px] uppercase tracking-[0.5em]">AUCUNE_GRANULE</p>
          </div>
        )}
      </div>
    </div>
  );
}

export function CapstoneManager({ darkMode, currentSlug }) {
  const { formData, updateFormData } = useParcoursCreator();  
  const [activeTab, setActiveTab] = useState("core");
  const path = "capstone";
  const capstone = formData.capstone || {};
  const stepsCount = capstone.resources?.steps?.length || 0;
  const objectifs = capstone.objectifs || [];
  
  // Tokens et classes réutilisables
  const dm = darkMode;
  const tk = tokens(dm);
  const inputCls = tk.input + " border outline-none transition-all rounded-md px-3 py-2.5 text-[13px] w-full";
  const labelCls = "text-[11px] font-black uppercase tracking-[0.2em] mb-2 block";
  
  const tabs = [
    { id: "core", label: "IDENTITÉ & BRIEF", icon: FileText },
    { id: "flux", label: "FLUX ÉPREUVE", icon: ListOrdered },
    { id: "warroom", label: "SALLE DE CRISE", icon: Cpu },
    { id: "final", label: "CLÔTURE", icon: Trophy },
  ];

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      {/* ── En-tête ── */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className={`text-2xl font-bold tracking-tight ${dm ? "text-white" : "text-zinc-900"}`}>
            Épreuve <span className="text-orange-500">finale</span>
          </h2>
          <p className={`text-[12px] mt-1 ${tk.textSub}`}>
            Configuration de l'épreuve terminale et du projet de synthèse
          </p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-[11px] font-medium ${dm ? "border-zinc-700 bg-zinc-900 text-zinc-400" : "border-zinc-200 bg-zinc-50 text-zinc-500"}`}>
          <div className="w-2 h-2 bg-orange-500 animate-pulse rounded-full" />
          PROTOCOLE_ID : {currentSlug || "NON_DÉFINI"}
        </div>
      </div>

      {/* Tabs */}
      <div className={`sticky top-0 z-40 flex items-center gap-1 p-1 mb-6  backdrop-blur-md ${dm ? "bg-zinc-950/90 border-white/5" : "bg-white/90 border-zinc-200"}`}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-[11px] font-black uppercase tracking-widest transition-all rounded-md ${activeTab === tab.id ? "bg-orange-500 text-white" : `${tk.textSub} hover:text-orange-400`}`}
          >
            <tab.icon size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contenu principal */}
      <div className="flex-1 overflow-y-auto overflow-x-auto no-scrollbar">
        {activeTab === "core" && (
          <div className="space-y-6">
            {/* Identité épreuve */}
            <WorkspaceCard
              title="Matrice d'identité"
              subtitle="Définition narrative et stratégique"
              icon={Terminal}
              accent="orange"
              required
              darkMode={dm}
              defaultOpen={true}
            >
              <div className="space-y-5">
                <div>
                  <FieldLabel label="Titre de l'épreuve" required darkMode={dm} />
                  <input
                    value={capstone.titre || ""}
                    onChange={(e) => updateFormData(`${path}.titre`, e.target.value)}
                    className={inputCls + " text-lg font-bold"}
                    placeholder="EX : DEVENEZ MAIRE DE L'ÉDUCATION"
                  />
                </div>
                <div>
                  <FieldLabel label="Briefing narratif" hint="Contexte, intention et enjeux" required darkMode={dm} />
                  <textarea
                    value={capstone.description || ""}
                    onChange={(e) => updateFormData(`${path}.description`, e.target.value)}
                    rows={6}
                    className={inputCls + " resize-none font-mono text-[13px]"}
                    placeholder="Décrivez le scénario final..."
                  />
                </div>
              </div>
            </WorkspaceCard>

            {/* Objectifs finaux */}
            <WorkspaceCard
              title="Objectifs terminaux"
              subtitle="Acquis critiques validés par l'épreuve"
              icon={Target}
              accent="rose"
              darkMode={dm}
              defaultOpen={true}
            >
              <div className="space-y-2">
                {objectifs.map((obj, i) => (
                  <div key={i} className={`flex group border rounded-md overflow-hidden ${dm ? "border-zinc-800" : "border-zinc-200"}`}>
                    <div className={`w-12 flex items-center justify-center text-[12px] font-mono font-bold ${dm ? "bg-zinc-900 text-zinc-500" : "bg-zinc-100 text-zinc-400"}`}>
                      {String(i + 1).padStart(2, "00")}
                    </div>
                    <input
                      value={obj || ""}
                      onChange={(e) => {
                        const newObj = [...objectifs];
                        newObj[i] = e.target.value;
                        updateFormData(`${path}.objectifs`, newObj);
                      }}
                      className={`flex-1 p-2.5 text-[13px] outline-none ${dm ? "bg-zinc-950 text-zinc-100" : "bg-white text-zinc-900"}`}
                      placeholder="Objectif final..."
                    />
                    <button
                      onClick={() => updateFormData(`${path}.objectifs`, objectifs.filter((_, idx) => idx !== i))}
                      className={`px-3 opacity-0 group-hover:opacity-100 transition-opacity ${dm ? "text-zinc-500 hover:text-red-400" : "text-zinc-400 hover:text-red-500"}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => updateFormData(`${path}.objectifs`, [...objectifs, ""])}
                  className={`mt-2 text-[11px] font-bold uppercase tracking-wide px-3 py-1.5 rounded-md border transition-all ${dm ? "border-orange-500/30 text-orange-400 hover:bg-orange-500/10" : "border-orange-200 text-orange-600 hover:bg-orange-50"}`}
                >
                  + Ajouter un objectif
                </button>
              </div>
            </WorkspaceCard>

            {/* Métriques */}
            <div className="grid grid-cols-3 gap-4">
              <WorkspaceCard title="Points" icon={Zap} accent="amber" darkMode={dm} defaultOpen={false}>
                <input
                  type="number"
                  value={capstone.points || 0}
                  onChange={(e) => updateFormData(`${path}.points`, Number(e.target.value))}
                  className={inputCls + " text-center font-bold text-orange-500"}
                />
              </WorkspaceCard>
              <WorkspaceCard title="Durée (min)" icon={Clock} accent="amber" darkMode={dm} defaultOpen={false}>
                <input
                  type="number"
                  value={capstone.duration || 0}
                  onChange={(e) => updateFormData(`${path}.duration`, Number(e.target.value))}
                  className={inputCls + " text-center"}
                />
              </WorkspaceCard>
              <WorkspaceCard title="Collaboration" icon={Users} accent="amber" darkMode={dm} defaultOpen={false}>
                <label className="flex items-center justify-between">
                  <span className="text-[13px]">Mode groupe</span>
                  <input
                    type="checkbox"
                    checked={capstone.groupCollaboration ?? false}
                    onChange={(e) => updateFormData(`${path}.groupCollaboration`, e.target.checked)}
                    className="accent-orange-500"
                  />
                </label>
              </WorkspaceCard>
            </div>
          </div>
        )}

        {activeTab === "flux" && (
          <ActivityStepsEditor path={path} darkMode={dm} isCapstone={true} />
        )}

        {activeTab === "warroom" && (
          <WorkspaceCard
            title="Interface salle de crise"
            subtitle="Modules collaboratifs en temps réel"
            icon={Cpu}
            accent="red"
            darkMode={dm}
            defaultOpen={true}
          >
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: "impactMap", label: "Carte d'impact" },
                { key: "petitionTracker", label: "Pétition en direct" },
                { key: "leaderboardNational", label: "Classement national" },
                { key: "mediaWall", label: "Mur social" },
              ].map(item => (
                <label key={item.key} className={`flex items-center gap-2 p-2 rounded-md border cursor-pointer transition-all ${dm ? "border-zinc-800 hover:bg-zinc-900" : "border-zinc-200 hover:bg-zinc-50"}`}>
                  <input
                    type="checkbox"
                    checked={capstone.centralWorkspace?.config?.[item.key] ?? false}
                    onChange={(e) => updateFormData(`${path}.centralWorkspace.config.${item.key}`, e.target.checked)}
                    className="accent-orange-500"
                  />
                  <span className="text-[12px] font-medium">{item.label}</span>
                </label>
              ))}
            </div>
          </WorkspaceCard>
        )}
      </div>
    </div>
  );
}

export function ProgressionManager({ darkMode }) {
  const { formData, updateFormData } = useParcoursCreator();
  
  const path = "engineConfig.progressionLogic";
  const currentSlug = formData.slug || "";
  const weights = formData.engineConfig?.progressionLogic?.weights || { quizzes: 40, activities: 30, capstone: 30 };
  const totalWeight = Number(weights.quizzes) + Number(weights.activities) + Number(weights.capstone);
  const isInvalid = totalWeight !== 100;

  const dm = darkMode;
  const tk = tokens(dm);
  const inputCls = tk.input + " border outline-none transition-all rounded-md px-3 py-2 text-[13px] w-full";

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      {/* En-tête */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className={`text-2xl font-bold tracking-tight ${dm ? "text-white" : "text-zinc-900"}`}>
            Logique de <span className="text-orange-500">progression</span>
          </h2>
          <p className={`text-[12px] mt-1 ${tk.textSub}`}>
            Pondération des phases et seuils de certification
          </p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-[11px] font-medium ${isInvalid ? "border-red-500 text-red-400" : dm ? "border-zinc-700 bg-zinc-900 text-zinc-400" : "border-zinc-200 bg-zinc-50 text-zinc-500"}`}>
          <Scale size={12} />
          Masse totale : {totalWeight}% {isInvalid && "⚠️ Erreur"}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Colonne gauche - Pondération */}
        <div className="col-span-12 lg:col-span-7 space-y-6">
          <WorkspaceCard
            title="Pondération des phases"
            subtitle="Répartition du score final"
            icon={Sliders}
            accent="sky"
            darkMode={dm}
            defaultOpen={true}
          >
            <div className="space-y-6">
              {[
                { id: "quizzes", label: "Phase 01 - Fondations", color: "sky", icon: BookOpen },
                { id: "activities", label: "Phase 02 - Pratique", color: "amber", icon: Activity },
                { id: "capstone", label: "Phase 03 - Épreuve", color: "emerald", icon: Trophy },
              ].map((item) => (
                <div key={item.id} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <item.icon size={14} className={`text-${item.color}-500`} />
                      <span className="text-[13px] font-medium">{item.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={weights[item.id] || 0}
                        onChange={(e) => updateFormData(`${path}.weights.${item.id}`, Number(e.target.value))}
                        className="w-16 text-right px-2 py-1 rounded-md border bg-transparent text-[13px]"
                      />
                      <span className="text-[12px] text-zinc-500">%</span>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={weights[item.id] || 0}
                    onChange={(e) => updateFormData(`${path}.weights.${item.id}`, Number(e.target.value))}
                    className={`w-full h-1.5 rounded-full appearance-none bg-zinc-200 dark:bg-zinc-800 accent-${item.color}-500`}
                  />
                </div>
              ))}
            </div>

            {/* Visualisation */}
            <div className="mt-6 pt-4  border-zinc-200 dark:border-zinc-800">
              <div className="flex h-2 rounded-full overflow-hidden">
                <div style={{ width: `${(weights.quizzes / (totalWeight || 1)) * 100}%` }} className="bg-sky-500 transition-all" />
                <div style={{ width: `${(weights.activities / (totalWeight || 1)) * 100}%` }} className="bg-amber-500 transition-all" />
                <div style={{ width: `${(weights.capstone / (totalWeight || 1)) * 100}%` }} className="bg-emerald-500 transition-all" />
              </div>
              <div className="flex justify-between mt-2 text-[10px] text-zinc-500">
                <span>Fondations</span>
                <span>Pratique</span>
                <span>Épreuve</span>
              </div>
            </div>
          </WorkspaceCard>

          {/* Seuil certification */}
          <WorkspaceCard
            title="Seuil de certification"
            subtitle="Score minimum pour valider le parcours"
            icon={ShieldAlert}
            accent="rose"
            darkMode={dm}
            defaultOpen={false}
          >
            <div className="flex items-center gap-4">
              <input
                type="number"
                value={formData.engineConfig?.progressionLogic?.minimumScoreToPass || 0}
                onChange={(e) => updateFormData(`${path}.minimumScoreToPass`, Number(e.target.value))}
                className={inputCls + " w-32 text-center text-xl font-bold"}
              />
              <span className="text-2xl font-bold text-rose-500">%</span>
              <p className={`text-[11px] ${tk.textSub} flex-1`}>
                En dessous de ce seuil, l'apprenant ne reçoit pas la certification
              </p>
            </div>
          </WorkspaceCard>
        </div>

        {/* Colonne droite - Configuration moteur */}
        <div className="col-span-12 lg:col-span-5 space-y-6">
          <WorkspaceCard
            title="Configuration moteur"
            subtitle="Comportement technique avancé"
            icon={Database}
            accent="zinc"
            darkMode={dm}
            defaultOpen={true}
          >
            <div className="space-y-3">
              {[
                { key: "isSequencial", label: "Navigation séquentielle", hint: "Déblocage étape par étape" },
                { key: "allowSkipQuiz", label: "Saut de quiz autorisé", hint: "Permet de passer un quiz sans réponse" },
                { key: "autoValidateOpenQuestions", label: "Auto-validation réponses libres", hint: "Pas de relecture humaine" },
                { key: "enableDataHarvesting", label: "Collecte données impact", hint: "Suivi PESACORE activé" },
              ].map(opt => (
                <label key={opt.key} className={`flex items-start gap-3 p-2 rounded-md cursor-pointer transition-colors ${dm ? "hover:bg-zinc-900" : "hover:bg-zinc-50"}`}>
                  <input
                    type="checkbox"
                    checked={formData.engineConfig?.[opt.key] ?? false}
                    onChange={(e) => updateFormData(`engineConfig.${opt.key}`, e.target.checked)}
                    className="accent-orange-500 mt-0.5"
                  />
                  <div>
                    <div className="text-[12px] font-medium">{opt.label}</div>
                    {opt.hint && <p className={`text-[10px] ${tk.textSub}`}>{opt.hint}</p>}
                  </div>
                </label>
              ))}
            </div>
          </WorkspaceCard>
        </div>
      </div>
    </div>
  );
}

export function ActivityManager({ moduleIndex, darkMode }) {
  const { formData, updateFormData } = useParcoursCreator();  
  const [activeTab, setActiveTab] = useState("config");
  const { control, register, watch, setValue } = useForm({ values: { modules: formData.modules || [] } });
  const [granuleIndex, setGranuleIndex] = useState(0);
  
  // Récupération des données
  const currentModule = formData.modules?.[moduleIndex] || {};
  const granules = currentModule.granules || [];
  const currentGranule = granules[granuleIndex] || {};
  const currentTitle = currentGranule.titre || "ACTIVITÉ_SANS_NOM";
  const objectifs = currentGranule.objectifs || [];
  
  const path = `modules.${moduleIndex}.granules.${granuleIndex}`;
  const dm = darkMode;
  const tk = tokens(dm);
  const inputCls = tk.input + " border outline-none transition-all rounded-md px-3 py-2.5 text-[13px] w-full";
  
  // Trouver la première activité valide au chargement
  useEffect(() => {
    const firstAct = granules.findIndex(g => g.type === "activity");
    if (firstAct !== -1) setGranuleIndex(firstAct);
  }, [moduleIndex, granules.length]);
  
  const tabs = [
    { id: "config", label: "STRUCTURE", icon: Settings2 },
    { id: "sequence", label: "SÉQUENCE", icon: ListOrdered },
    { id: "workspace", label: "UX STUDIO", icon: LayoutGrid },
    { id: "social", label: "MUR/LIVE", icon: Share2 },
    { id: "validation", label: "VALIDATION", icon: BrainCircuit },
  ];

  return (
    <div className="flex flex-col h-full w-full overflow-hidden bg-transparent">
      {/* ── En-tête épuré ── */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className={`text-2xl font-bold tracking-tight ${dm ? "text-white" : "text-zinc-900"}`}>
            Activity <span className="text-orange-500">designer</span>
          </h2>
          <p className={`text-[12px] mt-1 ${tk.textSub}`}>
            Node_Path : <span className="text-orange-500 font-mono">MOD_{String(moduleIndex + 1).padStart(2, "00")} / GRN_{String(granuleIndex + 1).padStart(2, "00")}</span>
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-[11px] font-medium ${dm ? "border-zinc-700 bg-zinc-900 text-zinc-400" : "border-zinc-200 bg-zinc-50 text-zinc-500"}`}>
            <Activity size={12} className="text-orange-500" />
            SYS_REF: {currentGranule.id || "TEMP"}
          </div>
          <div className="flex items-center gap-2 text-orange-500">
            <div className="w-2 h-2 bg-orange-500 animate-pulse rounded-full" />
            <span className="text-[10px] font-mono uppercase tracking-wider">ENGINE_READY</span>
          </div>
        </div>
      </div>

      {/* ── Barre de navigation granule + actions ── */}
      <div className={`sticky top-0 z-40 flex items-center justify-between p-2 mb-6  backdrop-blur-md ${dm ? "bg-zinc-950/90 border-white/5" : "bg-white/90 border-zinc-200"}`}>
        <div className="flex items-center gap-3">
          {/* Sélecteur de granule avec dropdown */}
          <div className="relative group">
            <button className={`flex items-center gap-2 px-3 py-1.5 rounded-md border transition-all text-[11px] font-black uppercase tracking-widest ${dm ? "border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-white" : "border-zinc-300 bg-zinc-50 hover:bg-zinc-100 text-zinc-900"}`}>
              <Activity size={14} className="text-orange-500" />
              {currentTitle || "SELECT_GRANULE"}
              <ChevronDown size={12} className="opacity-60" />
            </button>
            <div className="absolute top-full left-0 mt-1 w-64 hidden group-hover:block z-50">
              <div className={`border shadow-xl rounded-md p-1.5 ${dm ? "bg-zinc-900 border-zinc-700" : "bg-white border-zinc-300"}`}>
                {granules.map((field, index) => field.type === "activity" && (
                  <button
                    key={index}
                    onClick={() => setGranuleIndex(index)}
                    className={`p-2.5 w-full text-left border-l-2 transition-all text-[11px] font-black uppercase rounded-md ${granuleIndex === index ? "border-orange-500 bg-orange-500/10 text-orange-400" : "border-transparent text-zinc-500 hover:bg-white/5"}`}
                  >
                    <div className="text-[10px] font-mono opacity-50">STEP_{index + 1}</div>
                    <div className="truncate text-[12px]">{field.titre || "Sans nom"}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* Bouton supprimer */}
          <button
            onClick={() => {
              const newGranules = granules.filter((_, idx) => idx !== granuleIndex);
              updateFormData(`modules.${moduleIndex}.granules`, newGranules);
              setGranuleIndex(Math.max(0, granuleIndex - 1));
            }}
            className={`p-1.5 rounded-md transition-colors ${dm ? "text-zinc-500 hover:text-red-400" : "text-zinc-400 hover:text-red-500"}`}
          >
            <Trash2 size={14} />
          </button>
        </div>
        
        {/* Tabs principales */}
        <div className={`flex items-center p-0.5 rounded-md border ${dm ? "bg-black border-white/5" : "bg-zinc-100 border-zinc-300"}`}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-black transition-all tracking-[0.1em] rounded-md ${activeTab === tab.id ? "bg-orange-500 text-white" : `${tk.textSub} hover:text-orange-400`}`}
            >
              <tab.icon size={11} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Contenu principal ── */}
      <div key={path} className={`flex-1 overflow-y-auto overflow-x-auto rounded-lg border p-6 ${dm ? "bg-zinc-950 border-white/10" : "bg-white border-zinc-200"} no-scrollbar`}>
        <div className="w-full animate-in fade-in duration-300">
          
          {/* TAB 1 : CONFIGURATION (STRUCTURE) */}
          {activeTab === "config" && (
            <div className="space-y-6">
              {/* Carte identité principale */}
              <WorkspaceCard
                title="Matrice d'identité"
                subtitle="Désignation, objectifs et métriques"
                icon={FileText}
                accent="orange"
                required
                darkMode={dm}
                defaultOpen={true}
              >
                <div className="space-y-6">
                  {/* Titre */}
                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-[11px] font-mono text-orange-500 font-bold">01_</span>
                      <FieldLabel label="Désignation" required darkMode={dm} />
                    </div>
                    <input
                      value={currentGranule.titre || ""}
                      onChange={(e) => updateFormData(`${path}.titre`, e.target.value)}
                      className={`w-full text-2xl font-black uppercase outline-none p-3 border-l-4 transition-all rounded-md ${dm ? "text-white border-zinc-700 focus:border-orange-500 bg-zinc-900/30" : "text-zinc-900 border-zinc-300 focus:border-orange-500 bg-zinc-50/50"}`}
                      placeholder="SAISIR IDENTIFIANT..."
                    />
                  </div>
                  
                  {/* Métriques (XP, durée, mode) */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className={`p-3 rounded-md border ${dm ? "border-zinc-800" : "border-zinc-200"}`}>
                      <label className={`text-[10px] font-bold uppercase mb-1 block ${tk.textSub}`}>Valeur XP</label>
                      <div className="flex items-center gap-2">
                        <Zap size={14} className="text-orange-500" />
                        <input
                          type="number"
                          value={currentGranule.points || 0}
                          onChange={(e) => updateFormData(`${path}.points`, Number(e.target.value))}
                          className="bg-transparent text-xl font-mono font-black text-orange-500 outline-none w-full"
                        />
                      </div>
                    </div>
                    <div className={`p-3 rounded-md border ${dm ? "border-zinc-800" : "border-zinc-200"}`}>
                      <label className={`text-[10px] font-bold uppercase mb-1 block ${tk.textSub}`}>Durée (min)</label>
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-orange-500" />
                        <input
                          type="number"
                          value={currentGranule.duration || 0}
                          onChange={(e) => updateFormData(`${path}.duration`, Number(e.target.value))}
                          className="bg-transparent text-xl font-mono font-black text-orange-500 outline-none w-full"
                        />
                      </div>
                    </div>
                    <div className={`p-3 rounded-md border ${dm ? "border-zinc-800" : "border-zinc-200"}`}>
                      <label className={`text-[10px] font-bold uppercase mb-1 block ${tk.textSub}`}>Mode</label>
                      <div className="flex items-center justify-between">
                        <span className={`text-[12px] font-black uppercase ${currentGranule.groupCollaboration ? "text-amber-500" : "text-zinc-500"}`}>
                          {currentGranule.groupCollaboration ? "Multi" : "Solo"}
                        </span>
                        <input
                          type="checkbox"
                          checked={currentGranule.groupCollaboration ?? false}
                          onChange={(e) => updateFormData(`${path}.groupCollaboration`, e.target.checked)}
                          className="accent-orange-500 w-4 h-4"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Objectifs */}
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-mono text-orange-500 font-bold">02_</span>
                        <FieldLabel label="Objectifs pédagogiques" darkMode={dm} />
                      </div>
                      <button
                        type="button"
                        onClick={() => updateFormData(`${path}.objectifs`, [...(currentGranule.objectifs || []), ""])}
                        className={`text-[11px] font-bold uppercase px-3 py-1.5 rounded-md border transition-all ${dm ? "border-orange-500/30 text-orange-400 hover:bg-orange-500/10" : "border-orange-200 text-orange-600 hover:bg-orange-50"}`}
                      >
                        + Ajouter
                      </button>
                    </div>
                    <div className="space-y-2">
                      {objectifs.map((obj, i) => (
                        <div key={i} className={`flex group border rounded-md overflow-hidden ${dm ? "border-zinc-800" : "border-zinc-200"}`}>
                          <div className={`w-12 flex items-center justify-center text-[12px] font-mono font-bold ${dm ? "bg-zinc-900 text-zinc-500" : "bg-zinc-100 text-zinc-400"}`}>
                            {String(i + 1).padStart(2, "00")}
                          </div>
                          <input
                            value={obj || ""}
                            onChange={(e) => {
                              const n = [...objectifs];
                              n[i] = e.target.value;
                              updateFormData(`${path}.objectifs`, n);
                            }}
                            className={`flex-1 p-2.5 text-[13px] font-mono outline-none ${dm ? "bg-zinc-950 text-zinc-100" : "bg-white text-zinc-900"}`}
                            placeholder="Définir l'objectif..."
                          />
                          <button
                            type="button"
                            onClick={() => updateFormData(`${path}.objectifs`, objectifs.filter((_, idx) => idx !== i))}
                            className={`px-3 opacity-0 group-hover:opacity-100 transition-opacity ${dm ? "text-zinc-500 hover:text-red-400" : "text-zinc-400 hover:text-red-500"}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                      {objectifs.length === 0 && (
                        <div className={`text-center py-8 text-[11px] uppercase tracking-wider border border-dashed rounded-md ${dm ? "border-zinc-700 text-zinc-500" : "border-zinc-300 text-zinc-400"}`}>
                          Aucun objectif défini
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Description */}
                  <div>
                    <FieldLabel label="Description narrative" darkMode={dm} />
                    <textarea
                      value={currentGranule.description || ""}
                      onChange={(e) => updateFormData(`${path}.description`, e.target.value)}
                      rows={4}
                      className={`${inputCls} resize-none font-mono text-[13px] leading-relaxed`}
                      placeholder="Contexte, intention pédagogique, consignes générales..."
                    />
                  </div>
                </div>
              </WorkspaceCard>
              
              {/* Carte Parcours de secours (Alternative Path) */}
              <WorkspaceCard
                title="Parcours de secours"
                subtitle="Logique de repli et accessibilité"
                icon={Shield}
                accent="amber"
                darkMode={dm}
                defaultOpen={false}
              >
                <div className="space-y-4">
                  <div>
                    <FieldLabel label="Condition de déclenchement" darkMode={dm} />
                    <select
                      value={currentGranule.alternativePath?.type || "none"}
                      onChange={(e) => updateFormData(`${path}.alternativePath.type`, e.target.value)}
                      className={inputCls}
                    >
                      <option value="none">_ AUCUNE</option>
                      <option value="text-only">_ ÉCHEC MULTIMÉDIA</option>
                      <option value="simplified">_ ACCESSIBILITÉ</option>
                    </select>
                  </div>
                  <div>
                    <FieldLabel label="Routine de remplacement" hint="Contenu alternatif proposé" darkMode={dm} />
                    <textarea
                      value={currentGranule.alternativePath?.description || ""}
                      onChange={(e) => updateFormData(`${path}.alternativePath.description`, e.target.value)}
                      rows={6}
                      className={`${inputCls} font-mono text-[13px] resize-none`}
                      placeholder="// Procédure de repli en cas de défaillance technique ou besoin d'accessibilité..."
                    />
                  </div>
                </div>
              </WorkspaceCard>
            </div>
          )}
          
          {/* TAB 2 : SÉQUENCE (Mediators + Steps) */}
          {activeTab === "sequence" && (
            <div className="space-y-6">
              {/* Médiateurs au démarrage */}
              <WorkspaceCard
                title="Médiateurs d'entrée"
                subtitle="Signaux audiovisuels au lancement"
                icon={Radio}
                accent="red"
                darkMode={dm}
                defaultOpen={true}
              >
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => updateFormData(`${path}.mediatorOnStart`, [...(currentGranule.mediatorOnStart || []), { type: "audio", path: "" }])}
                      className={`text-[11px] font-bold uppercase px-3 py-1.5 rounded-md border transition-all ${dm ? "border-red-500/30 text-red-400 hover:bg-red-500/10" : "border-red-200 text-red-600 hover:bg-red-50"}`}
                    >
                      + Ajouter un signal
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-4">
                    {(currentGranule.mediatorOnStart || []).map((mediator, i) => (
                      <div key={i} className={`flex-1 min-w-[280px] p-4 rounded-md border ${dm ? "border-zinc-800 bg-black/40" : "border-zinc-200 bg-zinc-50"}`}>
                        <div className="flex gap-3">
                          <div className="flex-1 space-y-3">
                            <select
                              value={mediator.type || "audio"}
                              onChange={(e) => {
                                const n = [...(currentGranule.mediatorOnStart || [])];
                                n[i] = { ...n[i], type: e.target.value };
                                updateFormData(`${path}.mediatorOnStart`, n);
                              }}
                              className={`w-full text-[12px] font-bold uppercase p-2 rounded-md border ${dm ? "bg-zinc-900 border-zinc-700 text-orange-400" : "bg-white border-zinc-300 text-orange-600"}`}
                            >
                              <option value="audio">🔊 AUDIO LOOP</option>
                              <option value="video">🎬 VIDEO BACKGROUND</option>
                            </select>
                            <input
                              value={mediator.path || ""}
                              onChange={(e) => {
                                const n = [...(currentGranule.mediatorOnStart || [])];
                                n[i] = { ...n[i], path: e.target.value };
                                updateFormData(`${path}.mediatorOnStart`, n);
                              }}
                              className={`w-full text-[12px] font-mono p-2 rounded-md border ${dm ? "bg-zinc-900 border-zinc-700 text-white" : "bg-white border-zinc-300 text-zinc-900"}`}
                              placeholder="chemin/vers/la/ressource..."
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => updateFormData(`${path}.mediatorOnStart`, (currentGranule.mediatorOnStart || []).filter((_, idx) => idx !== i))}
                            className={`self-start mt-1 ${dm ? "text-zinc-500 hover:text-red-400" : "text-zinc-400 hover:text-red-500"}`}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </WorkspaceCard>
              
              {/* Architecture du flux (ActivityStepsEditor) */}
              <WorkspaceCard
                title="Architecture du flux"
                subtitle="Séquencement des étapes pédagogiques"
                icon={ListOrdered}
                accent="sky"
                darkMode={dm}
                defaultOpen={true}
              >
                <ActivityStepsEditor
                  control={control}
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  path={path}
                  darkMode={dm}
                />
              </WorkspaceCard>
            </div>
          )}
          
          {/* TAB 3 : UX STUDIO (WorkspaceStudio) */}
          {activeTab === "workspace" && (
            <WorkspaceCard
              title="Studio d'expérience"
              subtitle="Configuration de l'espace de travail"
              icon={LayoutGrid}
              accent="emerald"
              darkMode={dm}
              defaultOpen={true}
            >
              <WorkspaceStudio
                control={control}
                register={register}
                watch={watch}
                setValue={setValue}
                key={`workspace-${moduleIndex}-${granuleIndex}`}
                path={path}
                darkMode={dm}
              />
            </WorkspaceCard>
          )}
          
          {/* TAB 4 : MUR / LIVE (Social) */}
          {activeTab === "social" && (
            <WorkspaceCard
              title="Interface sociale"
              subtitle="Mur collaboratif et interactions en direct"
              icon={Share2}
              accent="purple"
              darkMode={dm}
              defaultOpen={true}
            >
              <div className="space-y-4">
                <div className={`p-4 rounded-md border text-center ${dm ? "border-zinc-800 bg-zinc-900/30" : "border-zinc-200 bg-zinc-50"}`}>
                  <Share2 size={24} className="mx-auto mb-2 opacity-50" />
                  <p className={`text-[12px] ${tk.textSub}`}>
                    Module Mur collaboratif et flux en direct
                  </p>
                  <p className="text-[11px] font-mono mt-2 text-orange-500">
                    Configuration à venir dans la prochaine version
                  </p>
                </div>
              </div>
            </WorkspaceCard>
          )}
          
          {/* TAB 5 : VALIDATION (QuizStudio) */}
          {activeTab === "validation" && (
            <WorkspaceCard
              title="Quiz de validation"
              subtitle="Évaluation finale de l'activité"
              icon={BrainCircuit}
              accent="rose"
              darkMode={dm}
              defaultOpen={true}
            >
              <QuizStudio
                control={control}
                register={register}
                watch={watch}
                setValue={setValue}
                path={`${path}.comprehensionQuiz`}
                darkMode={dm}
              />
            </WorkspaceCard>
          )}
          
        </div>
      </div>
      
      {/* ── Footer avec synchronisation ── */}
      <div className={`mt-6 pt-4  flex justify-between items-center ${dm ? "border-zinc-800" : "border-zinc-200"}`}>
        <div className="flex gap-4 items-center">
          <div className={`text-[10px] font-mono ${tk.textSub} uppercase`}>
            Registry: <span className="text-orange-500 font-mono">{path}</span>
          </div>
          <div className={`text-[10px] font-mono ${tk.textSub}`}>
            Statut: <span className="text-green-500">● SYNC_OK</span>
          </div>
        </div>
        <button
          className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white text-[11px] font-black uppercase tracking-widest rounded-md transition-all shadow-lg hover:shadow-orange-500/20"
        >
          Synchroniser l'activité
        </button>
      </div>
    </div>
  );
}

export function ModuleManager({ darkMode }) {
  
  const { formData, updateFormData, ui, setActiveModule } = useParcoursCreator();
  const modules = formData.modules || [];
  const activeModuleIdx = ui.activeModuleIdx;
  const [activePhase, setActivePhase] = useState("entree");
  const { control, register, watch, setValue } = useForm({ values: { modules: formData.modules || [] } });
  const { fields, append, remove } = useFieldArray({ control, name: "modules" });
  const { fields: fichesFields, append: addFiche, remove: removeFiche } = useFieldArray({ control, name: `modules.${activeModuleIdx}.fiches` });
  const currentModule = formData.modules?.[activeModuleIdx] || {};
  const hasFinalQuiz = !!currentModule?.finalQuiz;
  const currentTitle = currentModule?.titre || "Module sans titre";

  const handleAddModule = () => {
    const newModule = { titre: `Nouveau Module ${modules.length + 1}`, type: "Théorique/Pratique", description: "", objectifs: [], duree: 240, dureeDetaillee: { contenu: 120, activités: 100, quiz: 20 }, prerequis: "", competences: [], fiches: [], granules: [], diagnosticQuiz: { questions: [] }, finalQuiz: null, notes: "" };
    updateFormData('modules', [...modules, newModule]);
    append(newModule);
    setActiveModule(modules.length);
  };
  
  const handleDeleteModule = (idx) => {
    if (!window.confirm("Supprimer ce module et tout son contenu ?")) return;
    updateFormData('modules', modules.filter((_, i) => i !== idx));
    remove(idx);
    if (activeModuleIdx >= idx && activeModuleIdx > 0) setActiveModule(activeModuleIdx - 1);
    else if (activeModuleIdx >= modules.length - 1) setActiveModule(Math.max(0, modules.length - 2));
  };

  const phases = [
    { id: "entree", icon: DoorOpen, label: "STRUCTURE", sublabel: "Identité, objectifs, fiches" },
    { id: "diag", icon: Zap, label: "DIAGNOSTIC", sublabel: "Quiz d'entrée" },
    { id: "sequence", icon: Layers, label: "SÉQUENCE", sublabel: "Granules et flux" },
    { id: "final", icon: Target, label: "EXAMEN", sublabel: "Quiz final", hidden: !hasFinalQuiz },
  ].filter(p => !p.hidden);

  const dm = darkMode;
  const tk = tokens(dm);
  const inputCls = `${tk.input} border outline-none transition-all rounded-md px-3 py-2.5 text-[13px] w-full`;

  return (
    <div className="flex flex-col h-full w-full overflow-hidden bg-transparent">
      {/* ── En-tête épuré ── */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className={`text-2xl font-bold tracking-tight ${dm ? "text-white" : "text-zinc-900"}`}>
            Modules <span className="text-orange-500">du parcours</span>
          </h2>
          <p className={`text-[12px] mt-1 ${tk.textSub}`}>
            Chaque module est une séquence d'apprentissage avec ses granules et activités
          </p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-[11px] font-medium ${dm ? "border-zinc-700 bg-zinc-900 text-zinc-400" : "border-zinc-200 bg-zinc-50 text-zinc-500"}`}>
          <Layers size={12} className="text-orange-500" />
          SYS_REF: {modules.length} module{modules.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* ── Sélecteur de module — cartes horizontales ── */}
      {modules.length > 0 && (
        <div className="flex gap-2 mb-5 overflow-x-auto pb-2 flex-shrink-0">
          {modules.map((mod, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setActiveModule(idx)}
              className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-lg border text-[11px] font-black uppercase tracking-wider transition-all ${
                activeModuleIdx === idx
                  ? "bg-orange-500 text-white border-orange-500"
                  : dm
                    ? "bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-500"
                    : "bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300"
              }`}
            >
              <span className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                activeModuleIdx === idx
                  ? "bg-white/20 text-white"
                  : dm ? "bg-zinc-700 text-zinc-400" : "bg-zinc-200 text-zinc-500"
              }`}>{idx + 1}</span>
              <span className="truncate max-w-[120px]">{mod.titre || `MODULE_${idx + 1}`}</span>
              <span 
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); handleDeleteModule(idx); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleDeleteModule(idx); } }}
                className={`ml-1 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer ...`}
              >
                <X size={12} />
              </span>
            </button>
          ))}
        </div>
      )}

      {/* ── Phase tabs — design doux ── */}
      {modules[activeModuleIdx] && (
        <div className={`flex items-center p-0.5 rounded-md border mb-5 flex-shrink-0 ${dm ? "bg-black border-white/5" : "bg-zinc-100 border-zinc-300"}`}>
          {phases.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setActivePhase(p.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-black transition-all tracking-[0.1em] rounded-md ${
                activePhase === p.id
                  ? "bg-orange-500 text-white"
                  : `${tk.textSub} hover:text-orange-400`
              }`}
            >
              <p.icon size={11} />
              {p.label}
            </button>
          ))}
        </div>
      )}

      {/* ── Workspace principal ── */}
      <div className={`flex-1 overflow-y-auto overflow-x-auto rounded-lg border p-6 ${dm ? "bg-zinc-950 border-white/10" : "bg-white border-zinc-200"} no-scrollbar`}>
        {modules[activeModuleIdx] ? (
          <div className="w-full animate-in fade-in duration-300">

            {/* ════ PHASE ENTRÉE ════ */}
            {activePhase === "entree" && (
              <div className="space-y-6">
                <div className="grid grid-cols-12 gap-5">
                  <div className="col-span-12 lg:col-span-8 space-y-6">

                    {/* Identité — REQUIS */}
                    <WorkspaceCard
                      title="Matrice d'identité"
                      subtitle="Désignation, description et structure du module"
                      icon={Settings2}
                      accent="orange"
                      required
                      darkMode={dm}
                      defaultOpen={true}
                    >
                      <div className="space-y-6">
                        <div className="grid grid-cols-4 gap-4">
                          <div className="col-span-3">
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className="text-[11px] font-mono text-orange-500 font-bold">01_</span>
                              <FieldLabel label="Désignation" required darkMode={dm} />
                            </div>
                            <input
                              value={currentModule.titre || ''}
                              onChange={(e) => updateFormData(`modules.${activeModuleIdx}.titre`, e.target.value)}
                              className={`${inputCls} text-base font-medium`}
                              placeholder="SAISIR IDENTIFIANT..."
                            />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className="text-[11px] font-mono text-orange-500 font-bold">02_</span>
                              <FieldLabel label="Typologie" darkMode={dm} />
                            </div>
                            <select
                              value={currentModule.type || ''}
                              onChange={(e) => updateFormData(`modules.${activeModuleIdx}.type`, e.target.value)}
                              className={inputCls}
                            >
                              <option value="Théorique">📘 ENSEIGNEMENT</option>
                              <option value="Pratique">🛠️ FORMATION</option>
                              <option value="Théorique/Pratique">🎯 PARCOURS COMPLET</option>
                            </select>
                          </div>
                        </div>
                        
                        <div>
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="text-[11px] font-mono text-orange-500 font-bold">03_</span>
                            <FieldLabel label="Description narrative" darkMode={dm} />
                          </div>
                          <textarea
                            value={currentModule.description || ''}
                            onChange={(e) => updateFormData(`modules.${activeModuleIdx}.description`, e.target.value)}
                            rows={2}
                            className={inputCls + " resize-none"}
                            placeholder="Contexte, intention pédagogique, objectifs généraux..."
                          />
                        </div>

                        {/* Métriques temporelles */}
                        <div className={`p-4 rounded-md border ${dm ? "border-zinc-800" : "border-zinc-200"}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Clock size={14} className="text-orange-500" />
                              <span className={`text-[12px] font-black uppercase tracking-wider ${dm ? "text-zinc-300" : "text-zinc-700"}`}>Durée estimée</span>
                            </div>
                            <div className="flex items-baseline gap-1.5">
                              <input
                                value={currentModule.duree || ''}
                                type="number"
                                onChange={(e) => updateFormData(`modules.${activeModuleIdx}.duree`, e.target.value)}
                                className={`w-16 text-right text-lg font-black bg-transparent outline-none ${dm ? "text-orange-400" : "text-orange-600"}`}
                                placeholder="0"
                              />
                              <span className={`text-[12px] font-mono ${dm ? "text-zinc-500" : "text-zinc-400"}`}>min</span>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            {[
                              { label: '📚 CONTENU', key: 'contenu' }, 
                              { label: '⚡ ACTIVITÉS', key: 'activités' }, 
                              { label: '📝 ÉVALUATION', key: 'quiz' }
                            ].map((item) => (
                              <div key={item.key} className={`p-2 rounded-md border ${dm ? "bg-zinc-900/50 border-zinc-700" : "bg-zinc-50 border-zinc-200"}`}>
                                <span className={`text-[9px] font-black uppercase block mb-1 ${dm ? "text-zinc-500" : "text-zinc-400"}`}>{item.label}</span>
                                <div className="flex items-center gap-1">
                                  <input
                                    value={currentModule.dureeDetaillee?.[item.key] || 0}
                                    type="number"
                                    onChange={(e) => updateFormData(`modules.${activeModuleIdx}.dureeDetaillee.${item.key}`, Number(e.target.value))}
                                    className={`w-full text-[13px] font-black bg-transparent outline-none ${dm ? "text-orange-400" : "text-orange-600"}`}
                                  />
                                  <span className={`text-[10px] font-mono ${dm ? "text-zinc-600" : "text-zinc-400"}`}>min</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </WorkspaceCard>

                    {/* Fiches & Ressources */}
                    <WorkspaceCard
                      title="Ressources pédagogiques"
                      subtitle="Documents, vidéos et supports téléchargeables"
                      icon={FileText}
                      accent="amber"
                      darkMode={dm}
                      defaultOpen={false}
                    >
                      <div className="flex justify-end mb-4">
                        <button
                          type="button"
                          onClick={() => addFiche({ id: crypto.randomUUID(), titre: "", filePath: "", fileType: "application/pdf", category: "theorie", isConceptTool: true, sourceGranuleId: "" })}
                          className={`text-[11px] font-black uppercase px-3 py-1.5 rounded-md border transition-all ${dm ? "border-amber-500/30 text-amber-400 hover:bg-amber-500/10" : "border-amber-200 text-amber-600 hover:bg-amber-50"}`}
                        >
                          + AJOUTER UNE RESSOURCE
                        </button>
                      </div>
                      <div className="space-y-3 max-h-[360px] overflow-y-auto overflow-x-auto">
                        {fichesFields.length === 0 ? (
                          <div className={`py-10 text-center rounded-lg border-2 border-dashed ${dm ? "border-zinc-800 text-zinc-600" : "border-zinc-200 text-zinc-400"}`}>
                            <FileText size={24} className="mx-auto mb-2 opacity-40" />
                            <p className="text-[11px] font-mono uppercase">AUCUNE RESSOURCE</p>
                          </div>
                        ) : fichesFields.map((field, index) => (
                          <div key={field.id} className={`p-3 rounded-md border ${dm ? "border-zinc-800 bg-zinc-950/40" : "border-zinc-200 bg-zinc-50"}`}>
                            <div className="flex gap-2 items-center">
                              <input
                                value={fichesFields[index]?.titre || ''}
                                onChange={(e) => updateFormData(`modules.${activeModuleIdx}.fiches.${index}.titre`, e.target.value)}
                                placeholder="TITRE_DE_LA_RESSOURCE"
                                className={`flex-1 bg-transparent text-[12px] font-black uppercase outline-none ${dm ? "text-zinc-200" : "text-zinc-800"}`}
                              />
                              <select
                                value={fichesFields[index]?.category || ''}
                                onChange={(e) => updateFormData(`modules.${activeModuleIdx}.fiches.${index}.category`, e.target.value)}
                                className={`text-[10px] font-black uppercase px-2 py-1 rounded border ${dm ? "bg-zinc-800 border-zinc-700 text-zinc-300" : "bg-zinc-100 border-zinc-200 text-zinc-600"}`}
                              >
                                <option value="theorie">📖 THÉORIE</option>
                                <option value="bonus">⭐ BONUS</option>
                              </select>
                              <button type="button" onClick={() => removeFiche(index)} className={`${dm ? "text-zinc-500 hover:text-red-400" : "text-zinc-400 hover:text-red-500"}`}>
                                <Trash2 size={14} />
                              </button>
                            </div>
                            <input
                              value={fichesFields[index]?.filePath || ''}
                              onChange={(e) => updateFormData(`modules.${activeModuleIdx}.fiches.${index}.filePath`, e.target.value)}
                              placeholder="chemin/vers/la/ressource..."
                              className={`w-full mt-2 text-[11px] font-mono bg-transparent outline-none ${dm ? "text-zinc-500" : "text-zinc-400"}`}
                            />
                          </div>
                        ))}
                      </div>
                    </WorkspaceCard>

                    {/* Activation cognitive */}
                    <WorkspaceCard
                      title="Activation cognitive"
                      subtitle="Script d'introduction pour préparer l'apprenant"
                      icon={BrainCircuit}
                      accent="red"
                      darkMode={dm}
                      defaultOpen={false}
                    >
                      <textarea
                        value={currentModule.preActivityReflection?.text || ''}
                        onChange={(e) => updateFormData(`modules.${activeModuleIdx}.preActivityReflection.text`, e.target.value)}
                        rows={6}
                        className={`w-full p-4 text-[12px] font-mono rounded-md border resize-none outline-none leading-relaxed ${dm ? "bg-black/45 border-red-900/30 text-red-100/90" : "bg-white border-red-200 text-red-950"}`}
                        placeholder="// Script réflexif déclenché avant l'entrée dans le module..."
                      />
                    </WorkspaceCard>

                    {/* Notes système */}
                    <WorkspaceCard
                      title="Notes internes"
                      subtitle="Mémos techniques visibles uniquement par les concepteurs"
                      icon={Terminal}
                      accent="sky"
                      darkMode={dm}
                      defaultOpen={false}
                    >
                      <textarea
                        value={currentModule.notes || ''}
                        onChange={(e) => updateFormData(`modules.${activeModuleIdx}.notes`, e.target.value)}
                        className={`w-full min-h-[140px] p-4 text-[11px] font-mono rounded-md border resize-none outline-none leading-tight ${dm ? "bg-zinc-950/60 border-zinc-700/50 text-zinc-400" : "bg-zinc-50 border-zinc-200 text-zinc-600"}`}
                        rows={5}
                        placeholder="// Notes techniques, rappels, liens utiles..."
                      />
                    </WorkspaceCard>

                    {/* Architecture PESA — AVANCÉ (conservé intact) */}
                    <WorkspaceCard
                      title="Architecture PESACORE"
                      subtitle="Configuration avancée de l'impact sur la simulation PESACORE"
                      icon={BrainCircuit}
                      accent="amber"
                      darkMode={dm}
                      defaultOpen={false}
                    >
                      <div className="space-y-5">
                        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-[11px] font-medium ${dm ? "border-amber-500/40 bg-amber-500/5 text-amber-400" : "border-amber-300 bg-amber-50 text-amber-700"}`}>
                          <Zap size={11} /> Impact direct sur la simulation PESACORE
                        </div>
                        <div>
                          <FieldLabel label="Spine Conceptuelle" hint="Paradoxe central structurant le module" darkMode={dm} />
                          <textarea value={currentModule.conceptualSpine || ''} onChange={(e) => updateFormData(`modules.${activeModuleIdx}.conceptualSpine`, e.target.value)} rows={3} className={`w-full p-3 text-[12px] font-mono border rounded-md resize-none leading-relaxed ${dm ? "bg-black/40 border-amber-900/30 text-amber-100/80" : "bg-white border-amber-200 text-amber-950"}`} placeholder="Ce que l'apprenant croit au départ vs ce qu'il comprendra à la fin..." />
                        </div>
                        <div>
                          <FieldLabel label="Contrat Épistémique" hint="Ce que l'apprenant sera capable de faire après ce module" darkMode={dm} />
                          <textarea value={currentModule.epistemicContract || ''} onChange={(e) => updateFormData(`modules.${activeModuleIdx}.epistemicContract`, e.target.value)} rows={3} className={`w-full p-3 text-[12px] font-mono border rounded-md resize-none leading-relaxed ${dm ? "bg-black/40 border-amber-900/30 text-amber-100/80" : "bg-white border-amber-200 text-amber-950"}`} placeholder="À l'issue de ce module, l'apprenant est capable de..." />
                        </div>
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <FieldLabel label="Poids Harvest PESACORE" hint="Influence de ce module sur le profil PESACORE final" darkMode={dm} />
                            <span className={`text-[13px] font-bold ${dm ? "text-amber-400" : "text-amber-600"}`}>{parseFloat(currentModule.harvestWeight || 0).toFixed(2)}</span>
                          </div>
                          <input type="range" min="0" max="1" step="0.05" value={currentModule.harvestWeight || 0} onChange={(e) => updateFormData(`modules.${activeModuleIdx}.harvestWeight`, parseFloat(e.target.value))} className="w-full accent-amber-500" />
                          <p className={`text-[11px] mt-1 ${dm ? "text-zinc-600" : "text-zinc-400"}`}>Plus élevé = plus d'influence sur le profil de l'apprenant</p>
                        </div>

                        {/* JSON avancés */}
                        <div className={`pt-4 ${dm ? "border-white/5" : "border-zinc-200"}`}>
                          <div className="flex items-center justify-between mb-3">
                            <p className={`text-[11px] font-semibold ${dm ? "text-zinc-500" : "text-zinc-500"}`}>Champs JSON avancés</p>
                            <span className={`text-[10px] px-2 py-0.5 rounded ${dm ? "bg-zinc-800 text-zinc-500" : "bg-zinc-200 text-zinc-500"}`}>Valider en quittant le champ</span>
                          </div>
                          {[
                            { key: 'sociologicalFramework', label: 'Cadre Sociologique', type: 'array', placeholder: '[{"author": "Pierre Bourdieu","concept": "Violence symbolique"}]' },
                            { key: 'pesaFieldConnections', label: 'Connexions Familles PESACORE', type: 'array', placeholder: '[{"pesaFamily": "École","bridgeType": "primary"}]' },
                            { key: 'crossModuleLinks', label: 'Liens Inter-Modules', type: 'array', placeholder: '[{"moduleOrder": 2,"concept": "La ZPD"}]' },
                          ].map(({ key, label, placeholder }) => (
                            <div key={key} className="mb-4">
                              <label className={`text-[10px] font-semibold uppercase tracking-wider mb-1.5 block ${dm ? "text-zinc-500" : "text-zinc-500"}`}>{label} <span className="text-amber-400 normal-case font-normal">[array]</span></label>
                              <textarea
                                key={`${key}-${activeModuleIdx}`}
                                defaultValue={JSON.stringify(currentModule[key] || [], null, 2)}
                                onBlur={(e) => { try { updateFormData(`modules.${activeModuleIdx}.${key}`, JSON.parse(e.target.value)); } catch (err) { } }}
                                rows={5}
                                className={`w-full p-3 text-[10.5px] font-mono border rounded-md resize-y leading-tight ${dm ? "bg-black/60 border-zinc-700/50 text-zinc-300" : "bg-zinc-50 border-zinc-200 text-zinc-700"}`}
                                placeholder={placeholder}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </WorkspaceCard>
                  </div>

                  {/* Colonne droite */}
                  <div className="col-span-12 lg:col-span-4 space-y-6">
                    
                    {/* Objectifs REQUIS */}
                    <WorkspaceCard
                      title="Objectifs pédagogiques"
                      subtitle="Acquis visés par ce module"
                      icon={Target}
                      accent="orange"
                      required
                      darkMode={dm}
                      defaultOpen={true}
                    >
                      <div className="space-y-3">
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => updateFormData(`modules.${activeModuleIdx}.objectifs`, [...(currentModule.objectifs || []), ""])}
                            className={`text-[11px] font-black uppercase px-3 py-1.5 rounded-md border transition-all ${dm ? "border-orange-500/30 text-orange-400 hover:bg-orange-500/10" : "border-orange-200 text-orange-600 hover:bg-orange-50"}`}
                          >
                            + AJOUTER
                          </button>
                        </div>
                        <div className="space-y-2 max-h-[200px] overflow-y-auto overflow-x-auto">
                          {(currentModule.objectifs || []).map((objectif, idx) => (
                            <div key={`obj-${idx}`} className={`group flex gap-2 items-start p-2.5 rounded-md border ${dm ? "bg-zinc-900/50 border-zinc-800" : "bg-zinc-50 border-zinc-200"}`}>
                              <span className={`text-[10px] font-mono mt-1.5 flex-shrink-0 ${dm ? "text-zinc-600" : "text-zinc-400"}`}>{String(idx + 1).padStart(2, "00")}</span>
                              <textarea
                                value={objectif || ""}
                                onChange={(e) => { const n = [...currentModule.objectifs]; n[idx] = e.target.value; updateFormData(`modules.${activeModuleIdx}.objectifs`, n); }}
                                rows={1}
                                className={`flex-1 bg-transparent text-[12px] outline-none resize-none ${dm ? "text-zinc-200" : "text-zinc-700"}`}
                                placeholder="Définir l'objectif..."
                              />
                              <button type="button" onClick={() => updateFormData(`modules.${activeModuleIdx}.objectifs`, currentModule.objectifs.filter((_, i) => i !== idx))} className={`opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ${dm ? "text-zinc-500 hover:text-red-400" : "text-zinc-400 hover:text-red-500"}`}>
                                <Trash2 size={12} />
                              </button>
                            </div>
                          ))}
                          {(!currentModule.objectifs || currentModule.objectifs.length === 0) && (
                            <div className={`py-8 text-center rounded-lg border-2 border-dashed ${dm ? "border-zinc-800 text-zinc-600" : "border-zinc-200 text-zinc-400"}`}>
                              <p className="text-[11px] font-mono uppercase">AUCUN OBJECTIF DÉFINI</p>
                            </div>
                          )}
                        </div>

                        {/* Compétences */}
                        <div className={`pt-3 ${dm ? "border-zinc-800" : "border-zinc-200"}`}>
                          <FieldLabel label="Compétences" hint="Mots-clés séparés par des virgules" darkMode={dm} />
                          <input
                            value={(currentModule.competences || []).join(", ")}
                            onChange={(e) => { const c = e.target.value.split(",").map(s => s.trim()).filter(Boolean); updateFormData(`modules.${activeModuleIdx}.competences`, c); setValue(`modules.${activeModuleIdx}.competences`, c); }}
                            placeholder="Ex : analyse critique, argumentation"
                            className={inputCls}
                          />
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {(currentModule.competences || []).map((c, i) => c && (
                              <span key={i} className={`px-2 py-0.5 text-[11px] font-medium rounded-md border flex items-center gap-1 ${dm ? "bg-orange-500/10 text-orange-400 border-orange-500/20" : "bg-orange-50 text-orange-600 border-orange-200"}`}>
                                {c}
                                <button type="button" onClick={() => updateFormData(`modules.${activeModuleIdx}.competences`, currentModule.competences.filter((_, idx) => idx !== i))} className="ml-0.5 text-red-400 hover:text-red-500">×</button>
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </WorkspaceCard>

                    {/* Flux d'entrée — Prérequis & Styles d'apprentissage */}
                    <WorkspaceCard
                      title="Prérequis & profils"
                      subtitle="Conditions d'accès et styles d'apprentissage ciblés"
                      icon={Activity}
                      accent="sky"
                      darkMode={dm}
                      defaultOpen={false}
                    >
                      <div className="space-y-4">
                        <div>
                          <FieldLabel label="Prérequis" hint="Ce que l'apprenant doit déjà maîtriser" darkMode={dm} />
                          <textarea value={currentModule.prerequis || ''} onChange={(e) => updateFormData(`modules.${activeModuleIdx}.prerequis`, e.target.value)} className={`w-full p-3 text-[12px] font-mono border rounded-md resize-none outline-none ${dm ? "bg-black/20 border-zinc-800 text-zinc-300" : "bg-white border-zinc-200 text-zinc-700"}`} rows={2} placeholder="Ex : avoir terminé le Module 1" />
                        </div>
                        <div>
                          <FieldLabel label="Styles d'apprentissage" darkMode={dm} />
                          <div className="grid grid-cols-2 gap-1.5">
                            {["Visuel", "Réflexif", "Kinesthésique", "Auditif"].map(style => (
                              <label key={style} className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors ${dm ? "hover:bg-zinc-800" : "hover:bg-zinc-50"}`}>
                                <input
                                  type="checkbox"
                                  checked={(currentModule.stylesApprentissage || []).includes(style)}
                                  onChange={(e) => { const styles = currentModule.stylesApprentissage || []; const newStyles = e.target.checked ? [...styles, style] : styles.filter(s => s !== style); updateFormData(`modules.${activeModuleIdx}.stylesApprentissage`, newStyles); }}
                                  className="accent-orange-500 rounded"
                                />
                                <span className={`text-[12px] ${dm ? "text-zinc-400" : "text-zinc-600"}`}>{style}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </WorkspaceCard>
                  </div>
                </div>
              </div>
            )}

            {/* PHASE DIAGNOSTIC */}
            {activePhase === "diag" && (
              <div className="space-y-6">
                <WorkspaceCard
                  title="Quiz de diagnostic"
                  subtitle="Questions d'entrée pour évaluer le niveau initial"
                  icon={Zap}
                  accent="orange"
                  darkMode={dm}
                  defaultOpen={true}
                >
                  <QuizStudio control={control} register={register} watch={watch} setValue={setValue} path={`modules.${activeModuleIdx}.diagnosticQuiz`} darkMode={dm} title="DIAGNOSTIC D'ENTRÉE" />
                </WorkspaceCard>
              </div>
            )}

            {/* PHASE SÉQUENCE */}
            {activePhase === "sequence" && (
              <WorkspaceCard
                title="Architecture séquentielle"
                subtitle="Granules et flux pédagogique"
                icon={Layers}
                accent="emerald"
                darkMode={dm}
                defaultOpen={true}
              >
                <GranuleEditor control={control} register={register} watch={watch} setValue={setValue} modulePath={`modules.${activeModuleIdx}`} darkMode={dm} />
              </WorkspaceCard>
            )}

            {/* PHASE FINALE */}
            {activePhase === "final" && hasFinalQuiz && (
              <WorkspaceCard
                title="Validation finale"
                subtitle="Quiz de certification"
                icon={Target}
                accent="rose"
                darkMode={dm}
                defaultOpen={true}
              >
                <QuizStudio control={control} register={register} watch={watch} setValue={setValue} path={`modules.${activeModuleIdx}.finalQuiz`} darkMode={dm} title="QUIZ FINAL" />
              </WorkspaceCard>
            )}
          </div>
        ) : (
          <div className={`py-24 flex flex-col items-center justify-center text-center`}>
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-5 ${dm ? "bg-zinc-900" : "bg-zinc-100"}`}>
              <Layers size={28} className="text-orange-500 opacity-60" />
            </div>
            <p className={`text-[14px] font-black uppercase mb-1 ${dm ? "text-zinc-400" : "text-zinc-600"}`}>AUCUN MODULE CRÉÉ</p>
            <p className="text-[11px] font-mono mb-6">Commencez par ajouter un module pour structurer votre parcours</p>
            <button type="button" onClick={handleAddModule} className="flex items-center gap-2 px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-[11px] font-black uppercase tracking-widest rounded-md transition-all shadow-lg hover:shadow-orange-500/20">
              <Plus size={15} /> CRÉER LE PREMIER MODULE
            </button>
          </div>
        )}
      </div>
    </div>
  );
} 

export function ProtocolIdentity({ darkMode }) {
  const { formData, updateFormData, errors } = useParcoursCreator();
  const dm = darkMode;
  const tk = tokens(dm);
  
  // Classe d'input alignée avec ActivityManager
  const inputCls = `${tk.input} border outline-none transition-all rounded-md px-3 py-2.5 text-[13px] w-full`;

  return (
    <div className="flex flex-col h-full w-full overflow-hidden bg-transparent">
      {/* ── En-tête épuré ── */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className={`text-2xl font-bold tracking-tight ${dm ? "text-white" : "text-zinc-900"}`}>
            Identité <span className="text-orange-500">du parcours</span>
          </h2>
          <p className={`text-[12px] mt-1 ${tk.textSub}`}>
            Les informations de base qui définissent et identifient votre parcours
          </p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-[11px] font-medium ${dm ? "border-zinc-700 bg-zinc-900 text-zinc-400" : "border-zinc-200 bg-zinc-50 text-zinc-500"}`}>
          <Fingerprint size={12} className="text-orange-500" />
          SYS_REF: {formData.slug || "TEMP"}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-5">
        {/* Colonne gauche */}
        <div className="col-span-12 lg:col-span-8 space-y-4">

          {/* Identité principale — REQUIS */}
          <WorkspaceCard
            title="Matrice d'identité"
            subtitle="Désignation, description et identifiant unique"
            icon={Fingerprint}
            accent="orange"
            required
            darkMode={dm}
            defaultOpen={true}
          >
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[11px] font-mono text-orange-500 font-bold">01_</span>
                    <FieldLabel label="Désignation" required darkMode={dm} />
                  </div>
                  <input
                    value={formData.nom || ''}
                    onChange={(e) => updateFormData("nom", e.target.value)}
                    className={`${inputCls} text-base font-medium`}
                    placeholder="SAISIR IDENTIFIANT..."
                  />
                  {errors?.nom && <p className="text-red-400 text-[11px] mt-1">{errors.nom.message}</p>}
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[11px] font-mono text-orange-500 font-bold">02_</span>
                    <FieldLabel label="Slug" hint="URL unique" darkMode={dm} />
                  </div>
                  <div className="flex gap-1">
                    <input
                      value={formData.slug || ''}
                      onChange={(e) => updateFormData("slug", e.target.value)}
                      className={inputCls + " flex-1"}
                      placeholder="nom-du-parcours-v1"
                    />
                    <button
                      type="button"
                      onClick={() => updateFormData("slug", `parcours-${Date.now().toString(36)}`)}
                      className={`px-3 rounded-md border transition-colors flex-shrink-0 ${dm ? "bg-zinc-800 border-zinc-700 text-zinc-400 hover:text-white" : "bg-zinc-100 border-zinc-200 text-zinc-500 hover:bg-zinc-200"}`}
                      title="Générer un nouveau slug"
                    >
                      <RefreshCw size={14} />
                    </button>
                  </div>
                  {errors?.slug && <p className="text-red-400 text-[11px] mt-1">{errors.slug.message}</p>}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[11px] font-mono text-orange-500 font-bold">03_</span>
                  <FieldLabel label="Description narrative" required darkMode={dm} />
                </div>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => updateFormData("description", e.target.value)}
                  rows={3}
                  className={`${inputCls} resize-y font-mono text-[13px] leading-relaxed`}
                  placeholder="Contexte, intentions, présentation générale..."
                />
              </div>

              {/* Métriques temporelles */}
              <div className="grid grid-cols-3 gap-4">
                <div className={`p-3 rounded-md border ${dm ? "border-zinc-800" : "border-zinc-200"}`}>
                  <label className={`text-[10px] font-bold uppercase mb-1 block ${tk.textSub}`}>Date de lancement</label>
                  <div className="flex items-center gap-2">
                    <Calendar size={14} className="text-orange-500" />
                    <input type="date" value={formData.dateLancement || ''} onChange={(e) => updateFormData("dateLancement", e.target.value)} className="bg-transparent text-[13px] font-mono outline-none w-full" />
                  </div>
                </div>
                <div className={`p-3 rounded-md border ${dm ? "border-zinc-800" : "border-zinc-200"}`}>
                  <label className={`text-[10px] font-bold uppercase mb-1 block ${tk.textSub}`}>Date de fin</label>
                  <div className="flex items-center gap-2">
                    <Calendar size={14} className="text-orange-500" />
                    <input type="date" value={formData.dateFin || ''} onChange={(e) => updateFormData("dateFin", e.target.value)} className="bg-transparent text-[13px] font-mono outline-none w-full" />
                  </div>
                </div>
                <div className={`p-3 rounded-md border ${dm ? "border-zinc-800" : "border-zinc-200"}`}>
                  <label className={`text-[10px] font-bold uppercase mb-1 block ${tk.textSub}`}>Durée estimée (min)</label>
                  <div className="flex items-center gap-2">
                    <Clock size={14} className="text-orange-500" />
                    <input type="number" value={formData.dureeEstimee || ''} onChange={(e) => updateFormData("dureeEstimee", Number(e.target.value))} className="bg-transparent text-xl font-mono font-black text-orange-500 outline-none w-full" placeholder="570" />
                  </div>
                </div>
              </div>
            </div>
          </WorkspaceCard>

          {/* Taxonomie et visibilité */}
          <WorkspaceCard
            title="Taxonomie & visibilité"
            subtitle="Niveau, tags et statut de publication"
            icon={Tag}
            accent="amber"
            darkMode={dm}
            defaultOpen={false}
          >
            <div className="grid grid-cols-2 gap-5">
              <div className="space-y-4">
                <div>
                  <FieldLabel label="Statut" hint="État d'avancement du parcours" darkMode={dm} />
                  <select value={formData.status || 'draft'} onChange={(e) => updateFormData("status", e.target.value)} className={inputCls}>
                    <option value="public">✓ PUBLIÉ</option>
                    <option value="private">◉ EN COURS</option>
                    <option value="draft">● BROUILLON</option>
                  </select>
                </div>
                <div>
                  <FieldLabel label="Visibilité" darkMode={dm} />
                  <select value={formData.visibility || 'private'} onChange={(e) => updateFormData("visibility", e.target.value)} className={inputCls}>
                    <option value="public">🌍 PUBLIC</option>
                    <option value="private">🔒 PRIVÉ</option>
                  </select>
                </div>
                <div>
                  <FieldLabel label="Niveau" darkMode={dm} />
                  <select value={formData.niveau || 'intermédiaire'} onChange={(e) => updateFormData("niveau", e.target.value)} className={inputCls}>
                    <option value="débutant">🌱 DÉBUTANT</option>
                    <option value="intermédiaire">⚡ INTERMÉDIAIRE</option>
                    <option value="avancé">🎯 AVANCÉ</option>
                  </select>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <FieldLabel label="Tags" hint="Séparés par des virgules" darkMode={dm} />
                  <input value={(formData.tags || []).join(", ")} onChange={(e) => updateFormData("tags", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} placeholder="Éducation, Citoyenneté..." className={inputCls} />
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(formData.tags || []).map((tag, i) => tag && (
                      <span key={i} className={`px-2 py-0.5 text-[11px] rounded-md border flex items-center gap-1 ${dm ? "bg-orange-500/10 text-orange-400 border-orange-500/20" : "bg-orange-50 text-orange-600 border-orange-200"}`}>
                        {tag}<button type="button" onClick={() => updateFormData("tags", formData.tags.filter((_, idx) => idx !== i))} className="text-red-400">×</button>
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <FieldLabel label="Catégories" darkMode={dm} />
                  <input value={(formData.categories || []).join(", ")} onChange={(e) => updateFormData("categories", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} placeholder="Éducation, Société..." className={inputCls} />
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(formData.categories || []).map((cat, i) => cat && (
                      <span key={i} className={`px-2 py-0.5 text-[11px] rounded-md border flex items-center gap-1 ${dm ? "bg-amber-500/10 text-amber-400 border-amber-500/20" : "bg-amber-50 text-amber-600 border-amber-200"}`}>
                        {cat}<button type="button" onClick={() => updateFormData("categories", formData.categories.filter((_, idx) => idx !== i))} className="text-red-400">×</button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </WorkspaceCard>
        </div>

        {/* Colonne droite */}
        <div className="col-span-12 lg:col-span-4 space-y-4">
          
          {/* Récompenses */}
          <WorkspaceCard
            title="Système de récompense"
            subtitle="Points, certification et badges"
            icon={Trophy}
            accent="amber"
            darkMode={dm}
            defaultOpen={true}
          >
            <div className="space-y-4">
              <div className={`p-3 rounded-md border ${dm ? "border-zinc-800" : "border-zinc-200"}`}>
                <label className={`text-[10px] font-bold uppercase mb-1 block ${tk.textSub}`}>Valeur XP</label>
                <div className="flex items-center gap-2">
                  <Zap size={14} className="text-orange-500" />
                  <input type="number" value={formData.recompenses?.points || 0} onChange={(e) => updateFormData("recompenses.points", Number(e.target.value))} className="bg-transparent text-xl font-mono font-black text-orange-500 outline-none w-full" />
                </div>
              </div>
              <div>
                <FieldLabel label="Titre de certification" hint="Affiché sur le certificat" darkMode={dm} />
                <input value={formData.recompenses?.certificationTitle || ''} onChange={(e) => updateFormData("recompenses.certificationTitle", e.target.value)} className={inputCls} placeholder="Ex : Ingénieur Social" />
              </div>
            </div>
          </WorkspaceCard>

          {/* Compétences & Prérequis */}
          <WorkspaceCard
            title="Compétences & prérequis"
            subtitle="Acquis visés et conditions d'entrée"
            icon={List}
            accent="sky"
            darkMode={dm}
            defaultOpen={false}
          >
            <div className="space-y-4">
              <div>
                <FieldLabel label="Compétences visées" darkMode={dm} />
                <input value={(formData.competences || []).join(", ")} onChange={(e) => updateFormData("competences", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} placeholder="Lecture critique, Analyse..." className={inputCls} />
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(formData.competences || []).map((comp, i) => comp && (
                    <span key={i} className={`px-2 py-0.5 text-[11px] rounded-md border flex items-center gap-1 ${dm ? "bg-sky-500/10 text-sky-400 border-sky-500/20" : "bg-sky-50 text-sky-600 border-sky-200"}`}>
                      {comp}<button type="button" onClick={() => updateFormData("competences", formData.competences.filter((_, idx) => idx !== i))} className="text-red-400">×</button>
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <FieldLabel label="Prérequis" darkMode={dm} />
                <input value={(formData.prerequis || []).join(", ")} onChange={(e) => updateFormData("prerequis", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} placeholder="débutant, notions de base..." className={inputCls} />
              </div>
            </div>
          </WorkspaceCard>

          {/* Engine Config */}
          <WorkspaceCard
            title="Configuration moteur"
            subtitle="Comportement technique"
            icon={Settings2}
            accent="red"
            darkMode={dm}
            defaultOpen={false}
          >
            <div className="space-y-1">
              {[
                { key: "isSequencial", label: "Navigation séquentielle", hint: "L'apprenant doit terminer chaque étape avant de passer à la suivante" },
                { key: "allowSkipQuiz", label: "Autoriser les sauts de quiz", hint: "Permet de passer un quiz sans le remplir" },
                { key: "autoValidateOpenQuestions", label: "Validation auto des questions ouvertes", hint: "Les réponses libres sont validées automatiquement" },
                { key: "enableDataHarvesting", label: "Collecte de données d'impact", hint: "Active le suivi PESACORE des réponses" },
                { key: "showContextualResources", label: "Ressources contextuelles", hint: "Affiche des fiches selon la progression" },
              ].map(opt => (
                <label key={opt.key} className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${dm ? "hover:bg-zinc-800/50" : "hover:bg-zinc-50"}`}>
                  <input type="checkbox" checked={formData.engineConfig?.[opt.key] ?? false} onChange={(e) => updateFormData(`engineConfig.${opt.key}`, e.target.checked)} className="accent-orange-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className={`text-[12px] font-medium ${dm ? "text-zinc-300" : "text-zinc-700"}`}>{opt.label}</span>
                    {opt.hint && <p className={`text-[11px] mt-0.5 ${dm ? "text-zinc-600" : "text-zinc-400"}`}>{opt.hint}</p>}
                  </div>
                </label>
              ))}
            </div>
          </WorkspaceCard>
        </div>
      </div>
    </div>
  );
}

function MissionControl({ protocols, completionStatus, currentProtocol, setProtocol, formData, darkMode }) {
  const dm = darkMode;
  const tk = tokens(dm);
  const modules = formData.modules || [];

  // Descriptions humaines par protocole
  const protocolGuide = {
    1: { emoji: "🏷️", what: "Nommez et décrivez votre parcours", why: "C'est la première chose que verront vos apprenants", tip: "Un bon nom est court, clair et donne envie de commencer." },
    2: { emoji: "📚", what: "Organisez le contenu en modules", why: "Les modules sont les grandes étapes du parcours", tip: "Chaque module = un thème cohérent avec ses propres activités." },
    3: { emoji: "⚡", what: "Configurez les activités interactives", why: "Les activités font pratiquer et réfléchir l'apprenant", tip: "Une bonne activité a un objectif clair et une durée raisonnable." },
    4: { emoji: "📊", what: "Définissez comment la progression est calculée", why: "Cela détermine quand un apprenant obtient sa certification", tip: "La somme des pondérations doit toujours faire 100%." },
    5: { emoji: "🏁", what: "Créez l'épreuve finale du parcours", why: "Le capstone est la mise en pratique de tout ce qui a été appris", tip: "Pensez à un défi réaliste, pas juste un quiz." },
  };

  const guide = protocolGuide[currentProtocol];

  return (
    <div className={`flex flex-col h-full overflow-auto no-scrollbar ${dm ? "bg-zinc-950" : "bg-white"}`}>
      {/* Logo */}
      <div className={`p-5  ${dm ? "border-white/6" : "border-zinc-100"}`}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center shadow-lg shadow-orange-500/25">
            <Database size={14} className="text-white" />
          </div>
          <div>
            <div className={`text-[12px] font-bold ${dm ? "text-white" : "text-zinc-900"}`}>Constructeur PESACORE</div>
            <div className={`text-[10px] ${darkMode ? "text-zinc-500" : "text-zinc-400"}`}>Architecture de parcours</div>
          </div>
        </div>
      </div>

      {/* Completion globale */}
      <div className={`px-5 py-4  ${dm ? "border-white/6" : "border-zinc-100"}`}>
        <div className="flex items-center justify-between mb-2">
          <span className={`text-[11px] font-medium ${dm ? "text-zinc-500" : "text-zinc-400"}`}>Avancement</span>
          <span className={`text-[12px] font-bold ${
            completionStatus.globalPct >= 100 ? "text-emerald-500"
            : completionStatus.globalPct > 40 ? "text-amber-500"
            : dm ? "text-zinc-500" : "text-zinc-400"
          }`}>{completionStatus.globalPct}%</span>
        </div>
        <div className={`h-2 rounded-full overflow-hidden ${dm ? "bg-zinc-800" : "bg-zinc-200"}`}>
          <div
            className={`h-full rounded-full transition-all duration-700 ${
              completionStatus.globalPct >= 100 ? "bg-emerald-500"
              : completionStatus.globalPct > 40 ? "bg-amber-500"
              : "bg-zinc-500"
            }`}
            style={{ width: `${completionStatus.globalPct}%` }}
          />
        </div>
      </div>

      {/* Protocoles */}
      <div className="px-3 py-4 flex-1 overflow-y-auto overflow-x-auto">
        <p className={`text-[10px] font-semibold uppercase tracking-[0.2em] mb-3 px-2 ${dm ? "text-zinc-600" : "text-zinc-400"}`}>Étapes</p>

        <div className="space-y-1">
          {protocols.map((p, i) => {
            const completion = completionStatus.protocols[i];
            const isActive = currentProtocol === p.id;

            return (
              <button
                key={p.id}
                onClick={() => setProtocol(p.id)}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all text-left ${
                  isActive
                    ? dm
                      ? "bg-orange-500/12 border border-orange-500/20"
                      : "bg-orange-50 border border-orange-200"
                    : dm
                      ? "hover:bg-zinc-900 border border-transparent"
                      : "hover:bg-zinc-50 border border-transparent"
                }`}
              >
                <StatusRing status={completion?.status || "void"} size={22} />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-[12px] font-semibold ${isActive ? dm ? "text-orange-300" : "text-orange-700" : dm ? "text-zinc-400" : "text-zinc-600"}`}>
                      {p.label}
                    </span>
                    {completion?.status === "complete" && (
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${dm ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/5" : "text-emerald-600 border-emerald-300 bg-emerald-50"}`}>OK</span>
                    )}
                    {completion?.status === "partial" && (
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${dm ? "text-amber-400 border-amber-500/30 bg-amber-500/5" : "text-amber-600 border-amber-200 bg-amber-50"}`}>{completion.score}/{completion.max}</span>
                    )}
                  </div>
                  <p className={`text-[10px] mt-0.5 truncate ${dm ? "text-zinc-600" : "text-zinc-400"}`}>{p.hint}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Guide contextuel */}
        {guide && (
          <div className={`mt-5 mx-1 p-4 rounded-xl border ${dm ? "border-orange-500/15 bg-orange-500/5" : "border-orange-100 bg-orange-50/50"}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-base">{guide.emoji}</span>
              <span className={`text-[11px] font-semibold ${dm ? "text-orange-300" : "text-orange-700"}`}>
                {protocols.find(p => p.id === currentProtocol)?.label}
              </span>
            </div>
            <p className={`text-[11px] font-medium mb-1 ${dm ? "text-zinc-300" : "text-zinc-700"}`}>{guide.what}</p>
            <p className={`text-[11px] mb-2 ${dm ? "text-zinc-500" : "text-zinc-500"}`}>{guide.why}</p>
            <div className={`pt-2  text-[10px] italic ${dm ? "border-white/5 text-zinc-600" : "border-orange-100 text-zinc-400"}`}>
              💡 {guide.tip}
            </div>
          </div>
        )}

        {/* Modules rapides si P2/P3 */}
        {(currentProtocol === 2 || currentProtocol === 3) && modules.length > 0 && (
          <div className="mt-4">
            <p className={`text-[10px] font-semibold uppercase tracking-[0.2em] mb-2 px-2 ${dm ? "text-zinc-600" : "text-zinc-400"}`}>
              Modules ({modules.length})
            </p>
            <div className="space-y-0.5">
              {modules.map((mod, idx) => {
                const granuleCount = (mod.granules || []).length;
                return (
                  <div key={idx} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[11px] ${dm ? "text-zinc-500" : "text-zinc-500"}`}>
                    <span className={`w-4 h-4 rounded flex items-center justify-center text-[9px] font-bold flex-shrink-0 ${dm ? "bg-zinc-800 text-zinc-400" : "bg-zinc-100 text-zinc-500"}`}>{idx + 1}</span>
                    <span className="truncate flex-1">{mod.titre || `Module ${idx + 1}`}</span>
                    <span className={`text-[10px] ${dm ? "text-zinc-700" : "text-zinc-300"}`}>{granuleCount}g</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Footer stats */}
      <div className={`p-4  ${dm ? "border-white/6" : "border-zinc-100"}`}>
        <div className={`grid grid-cols-3 gap-2 text-center text-[11px]`}>
          {[
            { label: "Modules", value: modules.length },
            { label: "Granules", value: modules.reduce((s, m) => s + (m.granules || []).length, 0) },
            { label: "Activités", value: modules.reduce((s, m) => s + (m.granules || []).filter(g => g.type === "activity").length, 0) },
          ].map((stat, i) => (
            <div key={i} className={`p-2 rounded-lg ${dm ? "bg-zinc-900" : "bg-zinc-50"}`}>
              <div className={`font-bold text-[14px] ${dm ? "text-zinc-200" : "text-zinc-800"}`}>{stat.value}</div>
              <div className={`text-[10px] ${dm ? "text-zinc-600" : "text-zinc-400"}`}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL : ParcoursCreator 
// ─────────────────────────────────────────────────────────────────────────────


export default function ParcoursCreator() {
  const navigate = useNavigate();  
  const [localDarkMode, setLocalDarkMode] = useState(true);
  const [isInitialRender, setIsInitialRender] = useState(true);
  const [isArchModalOpen, setIsArchModalOpen] = useState(false);

  const {
    formData, ui, updateFormData, loadTemplate, saveParcours, handlePreview,
    setProtocol, errors, toggleArchModal
  } = useParcoursCreator();

  const isNavOpen = useParcoursCreator(state => state.ui.isNavOpen);
  const toggleNav = useParcoursCreator(state => state.toggleNav);

  useForm({ resolver: zodResolver(parcoursSchema), values: formData });

  // Complétion
  const completionStatus = useCompletionStatus(formData);

  useEffect(() => {
    if (!formData.slug) updateFormData("slug", `parcours-${Date.now().toString(36)}-${Math.random().toString(36).slice(2,7)}`);
  }, [formData.slug, updateFormData]);

  useEffect(() => {
    const timer = setTimeout(() => setIsInitialRender(false), 10000);
    return () => clearTimeout(timer);
  }, []);

  const handleSave = async () => {
    try {
      const slug = await saveParcours();
      toast.success("Compilation en cours...");
      setTimeout(() => navigate(`/parcours/${slug}`), 1800);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const protocols = [
    { id: 1, label: "IDENTITÉ", icon: Database, hint: "Nom, slug, description, métadonnées" },
    { id: 2, label: "MODULES", icon: Layers, hint: "Structure pédagogique et granules" },
    { id: 3, label: "ACTIVITÉS", icon: Activity, hint: "Configuration des activités interactives" },
    { id: 4, label: "PROGRESSION", icon: GitBranch, hint: "Pondérations et logique de certification" },
    { id: 5, label: "CAPSTONE", icon: Target, hint: "Épreuve finale et déploiement" },
  ];

  const dm = localDarkMode;
  const bgPage = dm ? "bg-[#04396E]" : "bg-zinc-50";

  // Hint contextuel par protocole
  const currentProtocolDef = protocols.find(p => p.id === ui.currentProtocol);
  const currentCompletion = completionStatus.protocols[ui.currentProtocol - 1];

  return (
    <div className={`h-screen flex flex-col ${bgPage} ${dm ? "text-red-100" : "text-red-400"} `}>

      {/* ═══════════════════════════════════════════════════════════════════
          HEADER PRINCIPAL
      ═══════════════════════════════════════════════════════════════════ */}
      <header className={`sticky top-0 z-50 px-5 py-3  flex justify-between items-center backdrop-blur-xl ${dm ? "bg-zinc-950/90 border-white/8" : "bg-white/90 border-zinc-200 shadow-sm"}`}>
        {/* Gauche */}
        <div className="flex items-center gap-4">
          {/* Toggle sidebar */}
          <button
            onClick={toggleNav}
            className={`relative p-2 rounded-lg border transition-all ${
              isNavOpen
                ? dm ? "bg-orange-500/15 border-orange-500/30 text-orange-300" : "bg-orange-50 border-orange-200 text-orange-600"
                : dm ? "border-zinc-800 text-zinc-500 hover:text-zinc-300 hover:border-zinc-700" : "border-zinc-200 text-zinc-500 hover:bg-zinc-50"
            }`}
            title="Afficher/masquer le panneau de navigation"
          >
            <AnimatePresence>
              {isInitialRender && !isNavOpen && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 1, 0] }}
                  transition={{ duration: 0.8, repeat: Infinity }}
                  className="absolute inset-0 bg-orange-500/15 rounded-lg pointer-events-none"
                />
              )}
            </AnimatePresence>
            {isNavOpen ? <X size={16} /> : <Menu size={16} />}
          </button>

          {/* Logo */}
          <div className="flex items-center gap-2.5">
            <div className={`p-1.5 rounded-lg border ${dm ? "bg-orange-500/10 border-orange-500/20" : "bg-orange-50 border-orange-200"}`}>
              <Database size={16} className="text-orange-500" />
            </div>
            <div>
              <div className={`text-[13px] font-bold ${dm ? "text-white" : "text-zinc-900"}`}>
                Constructeur de parcours
              </div>
              <div className={`text-[10px] ${dm ? "text-zinc-500" : "text-zinc-400"}`}>
                PESACORE — {formData.nom || "Nouveau parcours"}
              </div>
            </div>
          </div>

          {/* Protocole actif */}
          <div className={`hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-lg border ${dm ? "border-orange-500/20 bg-orange-500/8" : "border-orange-200 bg-orange-50"}`}>
            {currentProtocolDef && <currentProtocolDef.icon size={12} className="text-orange-500" />}
            <span className={`text-[12px] font-semibold ${dm ? "text-orange-300" : "text-orange-700"}`}>
              {currentProtocolDef?.label}
            </span>
            {currentCompletion && <StatusRing status={currentCompletion.status} size={16} />}
          </div>
        </div>

        {/* Centre : Template */}
        <div className="flex-1 max-w-sm mx-8">
          <div className="relative">
            <Layers size={14} className="absolute left-3 inset-y-0 my-auto text-zinc-400 pointer-events-none" />
            <select
              onChange={(e) => { if (e.target.value) { loadTemplate(templates[e.target.value]); toast.success("Template chargé !"); } }}
              defaultValue=""
              className={`w-full pl-9 pr-8 py-2 rounded-lg border text-[12px] outline-none appearance-none cursor-pointer transition-all ${dm ? "bg-zinc-900 border-zinc-700 text-zinc-300 focus:border-orange-400/50" : "bg-white border-zinc-200 text-zinc-700 focus:border-orange-300 shadow-sm"}`}
            >
              <option value="" disabled>Charger un modèle de parcours...</option>
              <optgroup label="Éducation"><option value="pensee-critique">Éducation &amp; Société</option></optgroup>
              <optgroup label="Orientation"><option value="orientation-identite">Orientation &amp; Autonomie</option></optgroup>
              <optgroup label="Formation"><option value="de-aes">Diplôme d'Etat AES</option></optgroup>
              <optgroup label="Entreprise">
                <option value="conflits-internes">Gestion de Conflits</option>
                <option value="evenementiel-developpement-local">Impact Local</option>
                
              </optgroup>
            </select>
            <ChevronDown size={13} className="absolute right-3 inset-y-0 my-auto text-zinc-400 pointer-events-none" />
          </div>
        </div>

        {/* Droite : Actions */}
        <div className="flex items-center gap-2">
          {/* Prévisualisation */}
          <div className={`flex items-center gap-0.5 p-1 rounded-lg border ${dm ? "border-zinc-800 bg-zinc-900/50" : "border-zinc-200 bg-zinc-50"}`}>
            <button
              onClick={() => setIsArchModalOpen(true)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium transition-all ${dm ? "hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200" : "hover:bg-white text-zinc-500 hover:text-zinc-700"}`}
              title="Vue architecture"
            >
              <Share2 size={13} /> Architecture
            </button>
            <button onClick={() => handlePreview('parcours')} className={`p-1.5 rounded-md transition-all ${dm ? "hover:bg-zinc-800 text-zinc-500" : "hover:bg-white text-zinc-400"}`} title="Prévisualiser le parcours"><Layout size={14} /></button>
            <button onClick={() => handlePreview('module')} className={`p-1.5 rounded-md transition-all ${dm ? "hover:bg-zinc-800 text-zinc-500" : "hover:bg-white text-zinc-400"}`} title="Prévisualiser un module"><Scan size={14} /></button>
            <button onClick={() => handlePreview('activity')} className={`p-1.5 rounded-md transition-all ${dm ? "hover:bg-zinc-800 text-zinc-500" : "hover:bg-white text-zinc-400"}`} title="Prévisualiser une activité"><Target size={14} /></button>
          </div>

          {/* Toggle dark/light */}
          <button
            onClick={() => setLocalDarkMode(prev => !prev)}
            className={`p-2 rounded-lg border transition-all ${dm ? 'bg-zinc-800 border-zinc-700 text-yellow-400 hover:bg-zinc-700' : 'bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50'}`}
            title={dm ? 'Passer en mode clair' : 'Passer en mode sombre'}
          >
            {dm ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" /></svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" /></svg>
            )}
          </button>

          {/* Fermer */}
          <button
  onClick={() => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/';
    }
  }}
  className={`p-2 rounded-lg border transition-all ${dm ? 'border-zinc-800 text-zinc-500 hover:border-red-500/40 hover:text-red-400 hover:bg-red-500/5' : 'border-zinc-200 text-zinc-500 hover:border-red-300 hover:text-red-500 hover:bg-red-50'}`}
  title="Fermer"
>
  <Power size={16} />
</button>
        </div>
      </header>

      {/* ═══════════════════════════════════════════════════════════════════
          LAYOUT PRINCIPAL : Sidebar + Workspace
      ═══════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 flex overflow-y-auto no-scrollbar">

        {/* Sidebar */}
        <AnimatePresence>
          {isNavOpen && (
            <motion.aside
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 288, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.22, ease: "easeInOut" }}
              className={`flex-shrink-0 overflow-y-auto no-scrollbar ${dm ? "border-white/6" : "border-zinc-200"}`}
            >
              <MissionControl
                protocols={protocols}
                completionStatus={completionStatus}
                currentProtocol={ui.currentProtocol}
                setProtocol={setProtocol}
                formData={formData}
                darkMode={dm}
              />
            </motion.aside>
          )}
        </AnimatePresence>

        {/* Workspace */}
        <main className={`flex-1 flex flex-col overflow-y-auto no-scrollbar ${dm ? "bg-zinc-950" : "bg-zinc-50"}`}>

          {/* Barre de contexte */}
          <WorkspaceContextBar
            protocolLabel={`${currentProtocolDef?.label || ""}`}
            protocolHint={currentProtocolDef?.hint}
            darkMode={dm}
            completion={currentCompletion}
          />

          {/* Navigation protocoles — barre top dans le workspace */}
          <div className={`flex items-center gap-0.5 px-6 py-2  ${dm ? "border-white/5 bg-zinc-950" : "border-zinc-200 bg-white"}`}>
            {protocols.map((p) => {
              const comp = completionStatus.protocols[p.id - 1];
              const isActive = ui.currentProtocol === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setProtocol(p.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[12px] font-medium transition-all ${
                    isActive
                      ? dm ? "bg-orange-500/12 text-orange-300 border border-orange-500/20" : "bg-orange-50 text-orange-700 border border-orange-200"
                      : dm ? "text-zinc-500 hover:text-zinc-300 border border-transparent" : "text-zinc-500 hover:text-zinc-700 border border-transparent hover:bg-zinc-100"
                  }`}
                >
                  <p.icon size={13} className={isActive ? "text-orange-500" : dm ? "text-zinc-600" : "text-zinc-400"} />
                  <span>{p.label}</span>
                  {comp && <StatusRing status={comp.status} size={14} />}
                </button>
              );
            })}
          </div>

          {/* Contenu principal */}
          <div className="flex-1 overflow-y-auto overflow-x-auto no-scrollbar p-6 md:p-8">
            <div className="max-w-7xl mx-auto">

              {ui.currentProtocol === 1 && <ProtocolIdentity darkMode={dm} />}
              {ui.currentProtocol === 2 && <ModuleManager setProtocol={setProtocol} darkMode={dm} />}
              {ui.currentProtocol === 3 && (
                <div className="space-y-8">
                  {(() => {
                    const currentModule = formData.modules?.[ui.activeModuleIdx];
                    const activityIdx = currentModule?.granules?.findIndex(g => g.type === "activity");
                    if (activityIdx !== -1 && activityIdx !== undefined) {
                      return <ActivityManager moduleIndex={ui.activeModuleIdx} granuleIndex={activityIdx} setCurrentProtocol={setProtocol} darkMode={dm} />;
                    }
                    return (
                      <div className={`p-16 rounded-xl border-2 border-dashed text-center ${dm ? "border-zinc-800" : "border-zinc-200"}`}>
                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 ${dm ? "bg-zinc-900" : "bg-zinc-100"}`}>
                          <Zap size={28} className="text-orange-400" />
                        </div>
                        <p className={`text-[14px] font-semibold mb-2 ${dm ? "text-zinc-300" : "text-zinc-700"}`}>
                          Aucune activité dans le Module {ui.activeModuleIdx + 1}
                        </p>
                        <p className={`text-[12px] mb-6 ${dm ? "text-zinc-500" : "text-zinc-400"}`}>
                          Créez d'abord une granule de type "ACTIVITÉ" dans l'onglet Modules
                        </p>
                        <button
                          onClick={() => setProtocol(2)}
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-[12px] font-medium rounded-lg transition-colors"
                        >
                          <Layers size={14} /> Aller aux Modules
                        </button>
                      </div>
                    );
                  })()}
                </div>
              )}

              {ui.currentProtocol === 4 && <ProgressionManager setProtocol={setProtocol} darkMode={dm} />}
              {ui.currentProtocol === 5 && <CapstoneManager setProtocol={setProtocol} darkMode={dm} currentSlug={formData.slug} />}


              {/* ── Footer navigation ── */}
              <footer className={`mt-16 pt-8  flex items-center justify-between ${dm ? "border-zinc-800" : "border-zinc-200"}`}>
                {/* Status */}
                <div className="flex items-center gap-5">
                  <div className={`text-[11px] ${dm ? "text-zinc-600" : "text-zinc-400"}`}>
                    <span className="block">Avancement global</span>
                    <span className={`font-bold text-[14px] ${
                      completionStatus.globalPct >= 100 ? "text-emerald-500"
                      : completionStatus.globalPct > 40 ? "text-amber-500"
                      : dm ? "text-zinc-400" : "text-zinc-600"
                    }`}>{completionStatus.globalPct}%</span>
                  </div>

                  {/* Mini pipeline status */}
                  <div className="hidden lg:flex items-center gap-1">
                    {completionStatus.protocols.map((p, i) => (
                      <div key={i} className="flex items-center gap-1">
                        <StatusRing status={p.status} size={16}/>
                        {i < 4 && <div className={`w-4 h-px ${dm ? "bg-zinc-700" : "bg-zinc-300"}`}/>}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Navigation / Deploy */}
                <div className="flex gap-4">
                  {ui.currentProtocol > 1 && (
                    <button type="button" onClick={()=>setProtocol(ui.currentProtocol-1)} className={`px-5 py-3 text-xs font-black uppercase tracking-[0.2em] border transition-all ${dm?"border-white/10 text-zinc-400 hover:text-white hover:border-white/30":"border-zinc-300 text-zinc-500 hover:text-zinc-900"}`}>
                      ← P0{ui.currentProtocol-1}
                    </button>
                  )}
                  {ui.currentProtocol < 5 ? (
                    <button type="button" onClick={()=>setProtocol(ui.currentProtocol+1)} className={`px-10 py-4 bg-red-500 hover:bg-red-400 text-white text-xs font-black uppercase tracking-[0.2em] shadow-[0_0_20px_rgba(239,68,68,0.3)] transition-all hover:-translate-y-0.5`}>
                      Protocole_{ui.currentProtocol+1} →
                    </button>
                  ) : (
                    <button onClick={handleSave} disabled={ui.isSaving} className="px-10 py-4 bg-gradient-to-r from-red-600 via-purple-600 to-red-600 text-white text-xs font-black uppercase tracking-[0.2em] transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:-translate-y-0.5">
                      {ui.isSaving ? "COMPILATION..." : "DÉPLOYER INSTANCE"}
                    </button>
                  )}
                </div>
              </footer>
            </div>
          </div>
        </main>
      </div>

      {/* Architecture Modal */}
      {isArchModalOpen && (
        <ArchitectureModal data={formData} onClose={()=>{setIsArchModalOpen(false); toggleArchModal && toggleArchModal(false);}} darkMode={dm}/>
      )}
    </div>
  );
}